/**
 * prepress.js — PR ⑬a (prepress_v1 contract v1.0 + backend reply 2026-07-31)
 *
 * Pure helpers for the prepress integration. No transport, no React — every
 * export here is a total function so `prepress.cjs` can mirror it for the node
 * test harness.
 *
 * Three jobs:
 *   1. COUNTING   — a placed *piece* is not a *carton*. Read `cartons_placed`.
 *   2. TILING     — utilization is only untrustworthy when a piece is worth
 *                   more than one carton. That is `prepress_tiled`, NOT
 *                   `prepress_applied` (which is true in plain mode too).
 *   3. ERRORS     — the backend now returns `detail: {code, message, piece}`.
 *                   Before this module `parseErrorDetail` fell through every
 *                   branch and rendered the literal string "Unknown error",
 *                   discarding both the cause and the offending carton name.
 *
 * Contract sources: FE_INTEGRATION_prepress §3/§4/§7,
 * REPLY_to_frontend_prepress §1.2/§2.1.
 */

/* ------------------------------------------------------------------ */
/* Modes                                                               */
/* ------------------------------------------------------------------ */

/** The three prepress modes. `pair`/`auto` are tiling modes (see TILING_MODES). */
export const PREPRESS_MODES = ['plain', 'pair', 'auto'];

/**
 * Modes that can emit a tile worth more than one carton. Requesting one of
 * these does NOT mean the result is tiled — `auto` may measure plain as the
 * winner. Only the response's `prepress_tiled` says what actually happened.
 */
export const TILING_MODES = ['pair', 'auto'];

/** Normalize a mode to 'plain'|'pair'|'auto', or null for "not tagged". */
export function normalizeMode(mode) {
  if (typeof mode !== 'string') return null;
  const m = mode.trim().toLowerCase();
  if (m === '' || m === 'none' || m === 'null') return null;
  return PREPRESS_MODES.includes(m) ? m : null;
}

/**
 * True if any carton in this request asked for a tiling mode.
 * Drives the `max_utilization` objective block (contract §4).
 */
export function requestsTiling(modes) {
  return toModeList(modes).some((m) => TILING_MODES.includes(m));
}

/**
 * True if any carton asked for `auto`. `auto` nests once per candidate
 * (20–30 s cold) and returns 400 `AutoRequiresAsync` on a sync route, so the
 * async→sync fallback MUST refuse to run when this is true (D7).
 */
export function requestsAuto(modes) {
  return toModeList(modes).some((m) => m === 'auto');
}

/** Coerce anything into a clean array of normalized modes/nulls. */
function toModeList(modes) {
  if (!Array.isArray(modes)) return [];
  return modes.map(normalizeMode);
}

/**
 * Modes the UI actually offers right now.
 *
 * `auto` IS offerable even with tiling disabled: rev 3 §3 confirms it is then
 * restricted to non-tiling candidates and can only return a plain result, so it
 * is production-safe. Check `prepress_tiled` on the RESPONSE to know what you
 * actually got.
 *
 * `pair` is absent. It is gated off server-side (`PREPRESS_ALLOW_TILING=false`
 * → 400 `TilingDisabled`) and that gate is a MANUFACTURING decision — a
 * die-maker must confirm the interlocked tile's enclosed scrap pocket strips at
 * production speed. Offering a control that can only produce an error teaches
 * operators to ignore errors. Prefer `capabilities.modes` over this constant
 * when the endpoint has answered.
 */
export const PREPRESS_UI_MODES = ['plain', 'auto'];

/** True if the UI is allowed to offer this mode in the current phase. */
export function isModeOfferable(mode) {
  return PREPRESS_UI_MODES.includes(normalizeMode(mode));
}

/**
 * Per-carton modes aligned to bench order, ready for buildVectorRequest.
 * @param {Array<{prepress?: string|null}>} cartons
 */
export function collectPrepressModes(cartons) {
  if (!Array.isArray(cartons)) return [];
  return cartons.map((c) => normalizeMode(c?.prepress));
}

/**
 * Names of the cartons that asked for prepress — the input to §6 verification.
 * Uses the same name the backend echoes in `report.cartons[].name`: the
 * source_id, i.e. the filename without its .pdf extension.
 */
export function taggedCartonNames(cartons) {
  if (!Array.isArray(cartons)) return [];
  return cartons
    .filter((c) => normalizeMode(c?.prepress) !== null)
    .map((c) => sourceIdOf(c))
    .filter(Boolean);
}

/**
 * filename → source_id, matching the backend's derivation byte for byte:
 *
 *   source_id = os.path.splitext(os.path.basename(filename))[0]
 *
 * Rev 4 §3 confirmed the extension IS stripped, and that only the FINAL one is.
 * Inner dots, spaces and parentheses survive: "a.b.c Hepaguard (200 ML).pdf"
 * → "a.b.c Hepaguard (200 ML)". We previously stripped `.pdf` specifically,
 * which agrees for PDFs but would diverge on any other extension — and a
 * divergence here reports "prepress did not run" on a run that did.
 */
export function sourceIdOf(carton) {
  const n = carton?.name;
  if (typeof n !== 'string' || n === '') return null;
  const base = n.split('/').pop().split('\\').pop();
  const dot = base.lastIndexOf('.');
  // A leading dot is part of the name, not an extension (splitext agrees).
  return dot > 0 ? base.slice(0, dot) : base;
}

/** Short label for a tag chip. */
export function modeLabel(mode) {
  const m = normalizeMode(mode);
  if (m === 'plain') return 'Prepress';
  if (m === 'pair') return 'Prepress · paired';
  if (m === 'auto') return 'Prepress · auto';
  return null;
}

/* ------------------------------------------------------------------ */
/* Wire serialization (contract §2b)                                   */
/* ------------------------------------------------------------------ */

/**
 * Serialize per-carton prepress modes into the `prepress` query parameter.
 *
 * The convention (shared with `allowed_rotations`): a BARE value applies to
 * every uploaded file; a JSON LIST aligns to file order, where `null` opts a
 * file out.
 *
 *   ['plain','plain']  → "plain"            (bare — every file)
 *   ['plain', null]    → '["plain",null]'   (positional)
 *   [null, null]       → null               (omit entirely → today's behaviour)
 *
 * Collapsing an all-identical list to the bare form matters beyond tidiness:
 * `kerf`/`margin_*` are omitted when zero so a job hashes identically to a
 * legacy job (server-side dedup key, contract §3.6). Emitting the shortest
 * equivalent form keeps that property stable for prepress too.
 *
 * @param {Array<string|null>} modes  aligned to carton/file order
 * @returns {string|null}  query value, or null to omit the parameter
 */
export function serializePrepress(modes) {
  const list = toModeList(modes);
  if (list.length === 0) return null;
  if (list.every((m) => m === null)) return null;

  const first = list[0];
  if (list.every((m) => m === first)) return first;   // bare — applies to all

  return JSON.stringify(list);                        // positional, nulls opt out
}

/*
 * `serializeAllowedRotations` was DELETED in ⑭h.
 *
 * Rev 8 §1 withdrew `allowed_rotations` from the client surface: rotation
 * policy is resolved by the backend from deployment configuration and sending
 * it is 400 EnginePolicyNotAccepted. Measured, four rotations vs [0,180] was
 * 125.0s vs 56.3s for an IDENTICAL layout — a large effect, which is exactly
 * why it belongs to the carton family and not to a per-job operator toggle.
 *
 * Deleted rather than left unused, and the encoding family below is now three
 * members and a broadcast rule with no member: `carton_type` is the only
 * broadcasting parameter left, so the asymmetry note lives on it now.
 */

/* ------------------------------------------------------------------ */
/* Engine policy + passthrough — refused on SIX routes (rev 11 §3.1)   */
/* ------------------------------------------------------------------ */

/**
 * Parameters no client may set, on any route. Rev 11 §3.1 + §6.5.
 *
 * ⑭h removed `allowed_rotations` and `time_limit` from the EXPORT builders,
 * which is where rev 8 had refused them. Rev 11 widens the refusal to six
 * routes — adding `POST /planner/plan`, `/planner/plan/async`,
 * `POST /planner/skus` and `PATCH /planner/config` — and adds a third
 * parameter, `required_passthrough` (§6.5), which decides WHICH COMPONENT
 * evaluates firm order lines and is therefore a deployment decision.
 *
 * Measured before writing this: the four planner builders are pass-through.
 * They forward `sheet`, `order_lines[]`, `sku_defs[]` and the config patch
 * verbatim, so any one of them WOULD carry a forbidden key if a caller put one
 * there. No call site does today — the planner sheet is assembled field by
 * field and the config patch iterates a nine-key allow-list — so this is a
 * guard against the next call site, not a live defect.
 *
 * That distinction is the point. ⑬h's defect was a parameter assembled
 * correctly and dropped at a wrapper, invisible for six PRs because every
 * builder read fine in isolation. The lesson taken then was to test the wire
 * rather than the builder; the same lesson says to strip at the wire rather
 * than trust every future caller to remember.
 *
 * Read the resolved values back from `engine_policy` / `required_ownership`.
 */
export const REFUSED_ENGINE_POLICY = Object.freeze([
  // Contract 1.7 added `multistart`. We never sent it — but this list is what
  // stops a value SOMEBODY ELSE put in state from riding along inside a
  // nested member, which is the whole reason the strip is deep. The backend
  // refuses it because it is a correctness switch: it reopens the engine's
  // _PARALLEL_CTX window, where two concurrent layouts share one global and
  // one nests the other's carton.
  'allowed_rotations', 'time_limit', 'multistart', 'required_passthrough',
]);

/** camelCase spellings of the same four, since our own state uses them. */
const REFUSED_CAMEL = Object.freeze([
  'allowedRotations', 'timeLimit', 'multistart', 'requiredPassthrough',
]);

/**
 * A copy of `obj` with every refused parameter removed, at any depth.
 *
 * Deep, because these keys ride INSIDE nested request members —
 * `sheet`, each `order_lines[]` entry, each `sku_defs[]` entry — not only at
 * the top level. Returns the input unchanged for non-objects so it can be
 * applied unconditionally at a builder's edge.
 *
 * Silent by design: it never throws and never warns. A caller that includes
 * one of these has made an ordinary mistake, and the correct outcome is the
 * request the backend accepts — not a client-side error the operator cannot
 * act on. `enginePolicyOffenders` exists for the test that proves the strip
 * is actually doing something.
 */
export function stripEnginePolicy(obj) {
  if (Array.isArray(obj)) return obj.map(stripEnginePolicy);
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (REFUSED_ENGINE_POLICY.includes(k) || REFUSED_CAMEL.includes(k)) continue;
    out[k] = stripEnginePolicy(v);
  }
  return out;
}

/**
 * Every refused key present in `obj`, at any depth, as dotted paths.
 *
 * Exists so a test can assert the strip removed something REAL rather than
 * asserting a clean payload stayed clean — which is the assertion shape that
 * has passed while doing nothing three times in this integration.
 */
export function enginePolicyOffenders(obj, prefix = '') {
  const found = [];
  const walk = (node, path) => {
    if (Array.isArray(node)) { node.forEach((v, i) => walk(v, `${path}[${i}]`)); return; }
    if (!node || typeof node !== 'object') return;
    for (const [k, v] of Object.entries(node)) {
      const p = path ? `${path}.${k}` : k;
      if (REFUSED_ENGINE_POLICY.includes(k) || REFUSED_CAMEL.includes(k)) found.push(p);
      else walk(v, p);
    }
  };
  walk(obj, prefix);
  return found;
}


/* ------------------------------------------------------------------ */
/* Counting (contract §4 — the rule that protects a print run)         */
/* ------------------------------------------------------------------ */

/**
 * The carton count for a layout result. THE ONLY carton count in the codebase.
 *
 * A pair tile is one piece and two cartons, so `n_placed` UNDER-reports in
 * pair mode — quote 9, ship 18. In plain mode and with no prepress at all
 * `cartons_placed === n_placed`, so this reads unconditionally with no
 * mode-awareness and no branching (contract §4, reply §1.1).
 *
 * The `n_placed` fallback is not a hedge: a backend old enough to omit
 * `cartons_placed` is one where tiling cannot exist, so `n_placed` is exactly
 * right there.
 */
export function cartonsPlaced(result) {
  if (!result) return 0;
  const c = result.cartons_placed;
  if (Number.isFinite(c)) return c;
  return Number.isFinite(result.n_placed) ? result.n_placed : 0;
}

/** Pieces actually nested. Geometry/diagnostics only — never a carton count. */
export function piecesPlaced(result) {
  return Number.isFinite(result?.n_placed) ? result.n_placed : 0;
}

/** True when a piece is worth more than one carton, so piece ≠ carton on screen. */
export function piecesDifferFromCartons(result) {
  return cartonsPlaced(result) !== piecesPlaced(result);
}

/* ------------------------------------------------------------------ */
/* Tiling gate (reply §1.2)                                            */
/* ------------------------------------------------------------------ */

/** True if prepress ran at all. TRUE IN PLAIN MODE — do not gate the bar on this. */
export function prepressApplied(result) {
  return result?.prepress_applied === true;
}

/**
 * True only when an emitted piece is worth more than one carton — i.e. when a
 * tile's enclosed scrap actually inflates gross utilization.
 *
 * This is the gate for demoting/qualifying utilization and for the
 * quoting-only labelling. It describes the OUTCOME, not the request: `auto`
 * can be asked for and return plain, in which case utilization is sound.
 * Never derive this from the mode the operator selected.
 */
export function prepressTiled(result) {
  return result?.prepress_tiled === true;
}

/**
 * Copy explaining why utilization can't be quoted on, or null when it can.
 * Null in plain mode — qualifying there degrades the normal path for nothing.
 */
export function utilizationCaveat(result) {
  if (!prepressTiled(result)) return null;
  return 'Gross utilization counts the scrap enclosed inside each tile, so it '
    + 'reads high for tiled layouts. Compare on sheets or overrun, not on this.';
}

/**
 * Tiled output is quoting-only until a die-maker confirms the enclosed scrap
 * pocket strips at production speed. No code change lifts that gate
 * (contract §3), so this label is the last thing between a tile and a plate.
 */
export function quotingOnlyNotice(result) {
  if (!prepressTiled(result)) return null;
  return 'Quoting only — this layout uses interlocked tiles and is not '
    + 'cleared for production until a die-maker signs off on strippability.';
}

/* ------------------------------------------------------------------ */
/* Verification (contract §6 — the failure mode is silent)             */
/* ------------------------------------------------------------------ */

/*
 * ⑭a: there is deliberately no FLAPPED_VERTEX_COUNT. It was 17 — the reference
 * carton's count, not a constant. The flapped blank's vertex count varies with
 * geometry, so any carton whose dieline differed would have reported a false
 * "prepress did not run" on the one card an operator checks before plating.
 *
 * ⑭k: and there is deliberately no BARE_L_VERTEX_COUNT either, for the same
 * reason one revision later. It was 6 — BL's unflapped count, not a constant.
 * rev 12 gives RTE 10 corners and STE 8 or 12, so every comparison against it
 * was answering a BL question about a carton that was not one. It is deleted
 * rather than left exported: ⑭a's own note says a dead constant still pinned
 * by an assertion is exactly how a wrong constant gets picked back up, and
 * this one was NOT dead — `blankHasFlaps` was reading it.
 */

/**
 * The carton type a piece is on the wire when nobody chose one.
 *
 * ⑭k. `?carton_type=` omitted means `BL` (contract `query_params.carton_type
 * .omitted`), so a carton carrying no type IS a BL as far as the server is
 * concerned — and every carton on this bench carries no type today, because
 * there is no picker yet. Every family lookup therefore has to resolve an
 * absent type to something, and this is the only answer that matches what the
 * request actually does.
 *
 * Pinned against the contract file in `contract.test.js`, so if backend ever
 * changes what an omitted parameter means, the diff says so rather than this
 * constant quietly disagreeing with the wire.
 */
export const DEFAULT_CARTON_TYPE = 'BL';

/**
 * The unflapped topologies each family takes, used ONLY when the deployment
 * publishes none.
 *
 * rev 12 §1: BL takes 6, RTE 10, STE 8 or 12. Those numbers live in
 * `capabilities.unflapped_vertex_counts` and are read from there — this
 * fallback exists so a deployment that predates rev 12 behaves exactly as it
 * did before, which for a BL-only deployment is "6 and nothing else".
 *
 * It is deliberately NOT the full rev 12 table. Hard-coding RTE and STE here
 * would offer the tag on a deployment that would then reject the type, which
 * is the failure `read_supported_values_from` exists to prevent.
 */
export const FALLBACK_UNFLAPPED_COUNTS = Object.freeze({ BL: [6] });

/** The carton family this report entry is, or the wire default when unstated. */
export function familyOf(entry) {
  const t = entry?.carton_type;
  return (typeof t === 'string' && t) ? t : DEFAULT_CARTON_TYPE;
}

/**
 * STE's detected closure-column variant, READ and never derived (rev 12 §1.3).
 *
 * Both real field cartons happen to line up — 12 vertices is `centre_column`
 * and 8 is `left_aligned` — so `len(v) === 12 ? 'centre_column' : …` would
 * pass every test written against those two samples and break on the first
 * 12-vertex left-aligned carton. The difference is where the tuck column sits,
 * which only the package can see. There is no inference here on purpose.
 */
export function cartonVariant(entry) {
  const v = entry?.variant;
  return (typeof v === 'string' && v) ? v : null;
}

/**
 * Positive verification that prepress actually ran for the cartons we tagged.
 *
 * Checked positively rather than by absence of error, because the failure the
 * backend shipped and fixed was silent: the job succeeded, the carton nested,
 * and nothing reported that prepress had been skipped (contract §6, reply §5).
 *
 * @param {object} result             layout response
 * @param {string[]} taggedNames      carton names we asked prepress for
 * @returns {{ ok: boolean, applied: boolean, missing: string[], reason: string|null }}
 */
export function verifyPrepressRan(result, taggedNames) {
  const tagged = Array.isArray(taggedNames) ? taggedNames.filter(Boolean) : [];
  if (tagged.length === 0) {
    return { ok: true, applied: false, missing: [], reason: null };
  }

  // Rev 4 §3: prefer the server's resolved modes over our own inference. If
  // `sources[].prepress_mode` is present and says a tagged carton resolved to
  // null, the tag never took effect — a more direct signal than the report.
  const resolved = resolvedModes(result);
  if (resolved.size > 0) {
    const notResolved = tagged.filter((n) => resolved.has(n) && resolved.get(n) === null);
    if (notResolved.length > 0) {
      return {
        ok: false,
        applied: prepressApplied(result),
        missing: notResolved,
        reason: `The server resolved these cartons to no prepress mode: ${notResolved.join(', ')}. `
          + 'They were nested as bare outlines — do not send this to plate.',
      };
    }
  }

  const applied = prepressApplied(result);
  if (!applied) {
    return {
      ok: false,
      applied: false,
      missing: tagged,
      reason: 'Prepress was requested but the response does not report it as applied. '
        + 'The bare outline may have been nested instead — do not send this to plate.',
    };
  }

  // report.cartons[] has an entry ONLY for tagged cartons, and a pair-mode
  // carton emits TWO pieces but ONE report entry. Correlate by name, never by
  // index (contract §8, reply §4).
  const entries = result?.prepress_report?.cartons;
  const seen = new Set(
    (Array.isArray(entries) ? entries : [])
      .map((c) => c?.name)
      .filter((n) => typeof n === 'string'),
  );
  const missing = tagged.filter((n) => !seen.has(n));

  if (missing.length > 0) {
    return {
      ok: false,
      applied: true,
      missing,
      reason: `Prepress ran, but no report entry came back for: ${missing.join(', ')}. `
        + 'Those cartons may have nested as bare outlines.',
    };
  }

  return { ok: true, applied: true, missing: [], reason: null };
}

/**
 * Look up a carton's prepress report entry BY NAME.
 * Index correlation is wrong: a pair carton is two pieces and one entry.
 */
export function reportEntryFor(result, name) {
  const entries = result?.prepress_report?.cartons;
  if (!Array.isArray(entries) || typeof name !== 'string') return null;
  return entries.find((c) => c?.name === name) || null;
}

/* ------------------------------------------------------------------ */
/* Report surfaces (contract §8) — PR ⑬c                               */
/* ------------------------------------------------------------------ */

/**
 * Body dimensions for the "228 × 157 body, four 57 mm panels" readout.
 *
 * Width is the sum of `panels_mm`, height is `wall_height_mm`. That is the only
 * reading that reproduces the contract's reference figures: Hepaguard has
 * panels 57/57/57/57 and the doc's example readout is "228 × 157" —
 * 4 × 57 = 228, and 157 is the wall height. Flagged for backend confirmation;
 * the panel list itself is displayed verbatim either way.
 *
 * @returns {{ widthMm: number, heightMm: number, panels: number[], uniform: boolean }|null}
 */
export function bodyReadout(entry) {
  // ⑭k / rev 12 §1: `panels_mm` is a LIST on BL and an OBJECT on the tuck
  // families ({face, side, panel_order_mm, closure}). Reading only the list
  // shape did not throw — it returned null, so the body readout simply
  // vanished from a tuck carton's report card with nothing saying why.
  const raw = entry?.panels_mm;
  let panels = null;
  let closure = null;
  if (Array.isArray(raw)) {
    panels = raw;
  } else if (raw && typeof raw === 'object') {
    panels = Array.isArray(raw.panel_order_mm) ? raw.panel_order_mm : null;
    closure = readClosure(raw.closure);
  }
  if (!Array.isArray(panels) || panels.length === 0) return null;
  const clean = panels.filter((p) => Number.isFinite(p));
  if (clean.length !== panels.length) return null;
  const widthMm = clean.reduce((a, b) => a + b, 0);
  const heightMm = Number.isFinite(entry?.wall_height_mm) ? entry.wall_height_mm : null;
  if (heightMm === null) return null;
  return {
    widthMm,
    heightMm,
    panels: clean,
    uniform: clean.every((p) => p === clean[0]),
    closure,
  };
}

/**
 * The tuck families' closure block — read defensively, on purpose.
 *
 * rev 12 §8 publishes no claim about what RTE reports here and tells us to
 * read it from the report rather than assume it mirrors BL. §1.2 says STE
 * reports `upper === lower` for the WHOLE family, so code assuming they differ
 * is wrong everywhere rather than at an edge — hence `same`, which lets the
 * readout state one figure instead of implying two independent ones.
 *
 * @returns {{ upperMm, lowerMm, same }|null}
 */
function readClosure(c) {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const upperMm = Number.isFinite(c.upper) ? c.upper : null;
  const lowerMm = Number.isFinite(c.lower) ? c.lower : null;
  if (upperMm === null && lowerMm === null) return null;
  return { upperMm, lowerMm, same: upperMm !== null && upperMm === lowerMm };
}

/** English count word for the panel readout, so it reads as prose not a list. */
const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];

/** "228 × 157 body, four 57 mm panels" — catches a wrong SKU before a sheet is cut. */
export function bodyReadoutText(entry) {
  const b = bodyReadout(entry);
  if (!b) return null;
  const n = b.panels.length;
  const word = COUNT_WORDS[n] || String(n);
  const panelPart = b.uniform
    ? `${word} ${fmtMm(b.panels[0])} mm panels`
    : `panels ${b.panels.map(fmtMm).join(' / ')} mm`;
  // rev 12 §1.2: one figure when the two closures agree, which is every STE.
  // Printing "closure 30 / 30" invites the reader to look for a difference
  // that the family does not have.
  let closurePart = '';
  if (b.closure) {
    if (b.closure.same) closurePart = `, closure ${fmtMm(b.closure.upperMm)} mm both ends`;
    else if (b.closure.upperMm !== null && b.closure.lowerMm !== null) {
      closurePart = `, closure ${fmtMm(b.closure.upperMm)} / ${fmtMm(b.closure.lowerMm)} mm`;
    } else {
      const only = b.closure.upperMm !== null ? b.closure.upperMm : b.closure.lowerMm;
      closurePart = `, closure ${fmtMm(only)} mm`;
    }
  }
  return `${fmtMm(b.widthMm)} × ${fmtMm(b.heightMm)} body, ${panelPart}${closurePart}`;
}

/** Local degree normaliser — prepress.js stays dependency-free so .cjs can mirror it. */
function normDeg(deg) {
  if (!Number.isFinite(deg)) return 0;
  let r = deg % 360;
  if (r < 0) r += 360;
  return r;
}

/** Trim trailing zeros so 57.0 reads as 57 and 28.5 stays 28.5. */
function fmtMm(n) {
  if (!Number.isFinite(n)) return '—';
  return String(Math.round(n * 100) / 100);
}

/**
 * The four flap dimensions, split by whether the operator may change them.
 *
 * `gf`/`tf` are adjustable within a range; `dust`/`bf` are DERIVED and
 * read-only. The UI must show the derived pair as visibly locked rather than
 * merely greyed — they are formulas, and an operator who reads them as
 * editable-but-disabled will file a bug.
 *
 * Ranges are read from the response and never hard-coded: they depend on the
 * carton and on any `gf_range_mm`/`tf_range_mm` override.
 *
 * @returns {Array<{key, label, valueMm, editable, min: number|null, max: number|null}>|null}
 */
export function flapDims(entry, caps) {
  const f = entry?.flap_dims_mm;
  if (!f || typeof f !== 'object' || Array.isArray(f)) return null;
  const family = familyOf(entry);
  const adjustable = adjustableFlapsFor(caps, family);
  const range = (k) => {
    const r = entry?.[`${k}_range_mm`] || f?.[`${k}_range_mm`];
    if (Array.isArray(r) && r.length === 2 && r.every(Number.isFinite)) {
      return { min: r[0], max: r[1] };
    }
    return { min: null, max: null };
  };
  // Published order first so a BL card reads exactly as it always has, then
  // anything else in the order the server sent it.
  const known = ['gf', 'tf', 'tuck_lip', 'tuck_flap', 'dust', 'bf'];
  const keys = Object.keys(f).filter((k) => Number.isFinite(f[k]));
  keys.sort((a, b) => {
    const ia = known.indexOf(a), ib = known.indexOf(b);
    return (ia === -1 ? known.length + keys.indexOf(a) : ia)
      - (ib === -1 ? known.length + keys.indexOf(b) : ib);
  });
  const out = keys.map((key) => {
    const canon = canonicalFlap(key);
    const derived = DERIVED_FLAPS.includes(canon);
    // THREE states, not two. `derived` is a published formula and reads as
    // locked; `adjustable` has a range the engine will honour; `unknown` is a
    // flap this deployment publishes no adjustability for and gets NO claim
    // either way. Collapsing unknown into derived is what put "derived" under
    // a tuck carton's `tuck_flap` — the ⑭i bottom-flap defect inverted.
    const adjustability = derived ? 'derived'
      : (adjustable.includes(canon) || adjustable.includes(key)) ? 'adjustable'
        : 'unknown';
    return {
      key,
      // ⑭f's rule: copy is written from a PUBLISHED name or not at all. Keys
      // outside rev 12 §1's table render verbatim rather than getting a label
      // invented from their spelling.
      label: FLAP_DIM_LABELS[canon] || FLAP_DIM_LABELS[key] || key,
      labelled: Boolean(FLAP_DIM_LABELS[canon] || FLAP_DIM_LABELS[key]),
      valueMm: f[key],
      adjustability,
      // Kept so a caller reading the old two-state shape cannot silently start
      // treating `unknown` as adjustable: only a real published range is true.
      editable: adjustability === 'adjustable',
      derived,
      ...(derived ? { min: null, max: null } : range(canon === 'gf' || canon === 'tf' ? canon : key)),
    };
  });
  return out.length ? out : null;
}

/**
 * Flap-dimension labels, from rev 12 §1's published key table and nothing else.
 *
 * `tuck_flap` is NOT BL's `tf` (rev 12 §1.1) — it is side depth plus lip — so
 * the two are labelled distinctly and the family chip on the report card says
 * which carton you are looking at.
 */
const FLAP_DIM_LABELS = {
  gf: 'Glue flap',
  tf: 'Tuck flap',
  tuck_lip: 'Tuck lip',
  tuck_flap: 'Tuck flap (side + lip)',
  dust: 'Dust flap',
  bf: 'Bottom flap',
};

/**
 * Quality badge for how the input drawing was normalised.
 *
 * Rev 3 §8.4 pinned two things we had wrong:
 *
 *   - There is no `pose.rotation` key and never was. The fallback chain is gone.
 *   - `rotation_deg` is the CORRECTION APPLIED, not the input's rotation. A
 *     drawing rotated +90° reports 270. So the wording is "orientation
 *     corrected by 270°" — "was rotated 270°" states the inverse and is wrong.
 *
 * `mirrored: true` means the operator gets the MIRROR carton. It can only be
 * true when `artwork_cross_crease` is off; with that flag on a mirrored input
 * raises PoseError instead. Surfaced prominently — it changes which carton you
 * get, unlike a rotation that was silently corrected.
 *
 * @returns {{ rotationDeg, mirrored, rewound, normalised, deviationUm, notes[] }|null}
 */
export function poseNotes(entry) {
  const pose = entry?.pose;
  const deviationUm = Number.isFinite(entry?.max_input_deviation_um)
    ? entry.max_input_deviation_um : null;
  if (!pose || typeof pose !== 'object') {
    return deviationUm === null ? null
      : { rotationDeg: null, mirrored: false, rewound: false, normalised: false, deviationUm, notes: [] };
  }
  const rot = Number.isFinite(pose.rotation_deg) ? pose.rotation_deg : null;
  const mirrored = pose.mirrored === true;
  const rewound = pose.rewound === true;
  const normalised = pose.normalised === true;
  const notes = [];
  if (rot !== null && normDeg(rot) !== 0) {
    notes.push(`Orientation corrected by ${fmtMm(normDeg(rot))}°.`);
  }
  if (rewound) notes.push('Winding direction corrected.');
  if (mirrored) {
    notes.push('This drawing is mirrored, so you get the mirror carton. Check it against the structural drawing before plating.');
  }
  // Return null when there is nothing to SAY, not merely when there is no pose
  // data. An empty badge is noise that trains operators to skip the block.
  if (notes.length === 0 && deviationUm === null) return null;
  return { rotationDeg: rot, mirrored, rewound, normalised, deviationUm, notes };
}

/**
 * Blank summary for the preview caption.
 *
 * Two shapes here were wrong before contract revision 3, and both failed
 * SILENTLY rather than throwing:
 *
 *   - `blank.vertices` is a COUNT (17), not the polygon. We read it as an array
 *     and got 0, which made `blankHasFlaps` false for a perfectly good dieline —
 *     firing the "flaps were not added, do not send to plate" warning on every
 *     successful run. A false alarm on that message is worse than none: it is
 *     the one warning that must stay credible.
 *   - `bbox_mm` is `[width, height]`, NOT `[x0,y0,x1,y1]`. Reading `bbox_mm[2]`
 *     reads garbage. Every emitted polygon is anchored at local origin, so a
 *     viewport 4-tuple is exactly `[0, 0, bbox_mm[0], bbox_mm[1]]`.
 *
 * The drawable polygon is NOT here — it is `payload.piece_types[].vertices`.
 *
 * @returns {{ widthMm, heightMm, areaMm2, fingerprint, vertexCount }|null}
 */
export function blankSummary(entry) {
  const b = entry?.blank;
  if (!b || typeof b !== 'object') return null;
  const bbox = b.bbox_mm;
  const has2 = Array.isArray(bbox) && bbox.length === 2 && bbox.every(Number.isFinite);
  return {
    widthMm: has2 ? bbox[0] : null,
    heightMm: has2 ? bbox[1] : null,
    areaMm2: Number.isFinite(b.area_mm2) ? b.area_mm2 : null,
    fingerprint: typeof b.fingerprint === 'string' ? b.fingerprint : null,
    vertexCount: Number.isFinite(b.vertices) ? b.vertices : 0,
  };
}

/**
 * Viewport 4-tuple for the preview, derived from the 2-element bbox.
 * Exact because emitted polygons are origin-anchored (rev 3 §8.1).
 */
export function blankViewBox(entry) {
  const s = blankSummary(entry);
  if (!s || s.widthMm === null || s.heightMm === null) return null;
  return [0, 0, s.widthMm, s.heightMm];
}

/** "240 × 264.5 mm blank" for the preview caption. */
export function blankSummaryText(entry) {
  const s = blankSummary(entry);
  if (!s || s.widthMm === null || s.heightMm === null) return null;
  return `${fmtMm(s.widthMm)} × ${fmtMm(s.heightMm)} mm blank`;
}

/**
 * Did this carton's emitted dieline actually gain flaps?
 *
 * §6's second check. A blank still carrying its unflapped corner count means
 * the flaps were never added, however healthy the rest of the response looks.
 *
 * ⑭k — THIS COMPARED AGAINST A HARD-CODED 6 AND THAT WAS A LIVE DEFECT.
 *
 * The threshold was BL's unflapped count. rev 12 §1 gives RTE 10 corners and
 * STE 8 or 12, so an RTE nested UNFLAPPED comes back with 10 vertices, 10 is
 * greater than 6, and this returned `true` — "flaps were added" — for a carton
 * that was about to be cut as a bare outline. The one check that exists to
 * catch a silent prepress skip was itself silent on two of the three families.
 * The contract says so in its own words: *"the flapped blank's vertex count
 * VARIES WITH GEOMETRY … compare against the input instead."*
 *
 * So the threshold is now the family's OWN published unflapped count, read
 * from capabilities. `verification.input_vertex_count` in the contract file is
 * still the scalar `6` and is deliberately not used — it is the BL answer and
 * this reads three families.
 *
 * TRI-STATE, because the honest third answer exists. A family this deployment
 * publishes no topology for cannot be checked at all, and returning `false`
 * there would fire "do not send it to plate" on a perfectly good dieline —
 * a false alarm on the one warning that has to stay credible.
 *
 * @returns {boolean|null}  null = this deployment gave us nothing to check against
 */
export function blankHasFlaps(entry, caps) {
  const n = blankSummary(entry)?.vertexCount ?? 0;
  if (!(n > 0)) return null;
  const counts = unflappedCountsFor(caps, familyOf(entry));
  if (!counts || counts.length === 0) return null;
  return n > Math.max(...counts);
}

/* ------------------------------------------------------------------ */
/* Auto selection + tiling capability (§8, §3) — PR ⑬d                 */
/* ------------------------------------------------------------------ */

/**
 * Explicit prepress error codes we branch on.
 *
 * ⑭a: the SESSION-STATE inference that used to live here is gone. It read
 * `prepress_tiled: true` off a successful result and concluded the deployment
 * gate was open — inferring a safety posture from an outcome, which is the same
 * reasoning that produced a gate later found inert. `capabilities.allow_tiling`
 * is authoritative and `capabilities.modes` already omits `pair` when the gate
 * is shut, so there is nothing left to discover.
 *
 * These two remain because they READ an explicit `code` off an error body.
 * That is not inference.
 */

/** True when this error body is specifically the manufacturing gate. */
export function isTilingDisabledError(body) {
  return parsePrepressError(body)?.code === 'TilingDisabled';
}

/** True when `auto` was sent to a route that cannot run it. */
export function isAutoRequiresAsyncError(body) {
  return parsePrepressError(body)?.code === 'AutoRequiresAsync';
}

/**
 * `candidates[]` arrives in EVALUATION order, not ranked (§8.5). Sorting is the
 * caller's job and matching is by `key` — position carries no meaning.
 *
 * Rev 3 pinned exactly five fields: `key`, `cartons`, `pieces_placed`,
 * `eval_ms`, `audits_ok`. **`sheets`, `utilization` and `score` do not exist.**
 * We previously sorted on `score`, which meant we silently never sorted at all.
 *
 * So the rank is `cartons` descending — true cartons, tile-aware, and the only
 * field on the row that expresses "better". `utilization` is absent by design:
 * it is gross and tile-inflated, which is the same reason `max_utilization` is
 * rejected as an objective with tiling modes.
 */
export function sortedCandidates(selection) {
  const list = selection?.candidates;
  if (!Array.isArray(list)) return [];
  const clean = list.filter((c) => c && typeof c === 'object');
  const rankable = clean.every((c) => Number.isFinite(c.cartons));
  if (!rankable) return clean.slice();
  return clean.slice().sort((a, b) => {
    if (b.cartons !== a.cartons) return b.cartons - a.cartons;
    // Tie on cartons: the cheaper evaluation is the better answer.
    const ae = Number.isFinite(a.eval_ms) ? a.eval_ms : Infinity;
    const be = Number.isFinite(b.eval_ms) ? b.eval_ms : Infinity;
    return ae - be;
  });
}

/** The five fields a candidate row is guaranteed to carry (rev 3 §8.5). */
export const CANDIDATE_FIELDS = ['key', 'cartons', 'pieces_placed', 'eval_ms', 'audits_ok'];

/** Find a candidate by `key`. Never by index — evaluation order is not rank. */
export function candidateByKey(selection, key) {
  if (typeof key !== 'string' || key === '') return null;
  return sortedCandidates(selection).find((c) => c.key === key) || null;
}

/**
 * Header for the cartons column in the candidates table.
 *
 * When `scope == "job"` the figure counts cartons across the WHOLE job, not per
 * sheet — labelling it plain "cartons" would read as a per-sheet number and
 * silently overstate or understate by the sheet count (§8).
 */
export function cartonsColumnLabel(selection) {
  // scope is exactly "job" or "isolated" (rev 3 §8.5). Under "job" the figure
  // is the WHOLE job's carton count, so a plain "cartons" header would read as
  // per-sheet and mis-state it by the sheet count.
  return selection?.scope === 'job' ? 'job cartons' : 'cartons';
}

/**
 * Everything the selection panel needs, or null when `auto` did not run.
 *
 * `reason` is written by the backend to be displayed VERBATIM — it is not a
 * code to map or a sentence to rewrite.
 *
 * @returns {{ winner: string|null, reason: string|null, scope: string|null,
 *             cartonsLabel: string, candidates: object[], winnerCandidate: object|null }|null}
 */
export function selectionSummary(result) {
  const sel = result?.prepress_report?.selection;
  if (!sel || typeof sel !== 'object') return null;
  const winner = typeof sel.winner === 'string' && sel.winner ? sel.winner : null;
  const candidates = sortedCandidates(sel);
  if (!winner && candidates.length === 0) return null;
  return {
    winner,
    reason: typeof sel.reason === 'string' && sel.reason ? sel.reason : null,
    scope: typeof sel.scope === 'string' ? sel.scope : null,
    cartonsLabel: cartonsColumnLabel(sel),
    candidates,
    winnerCandidate: winner ? candidateByKey(sel, winner) : null,
  };
}

/**
 * Does this bench need the async route?
 *
 * `auto` nests the sheet once per candidate — 20–30 s cold — so an interactive
 * request would either time out at the gateway or block the UI for half a
 * minute. The backend rejects it outright with `AutoRequiresAsync`.
 */
export function requiresAsync(modes) {
  return requestsAuto(modes);
}

/** Expected wait, for setting the operator's expectation before a run. */
export function estimatedRunNote(modes) {
  if (!requestsAuto(modes)) return null;
  return 'Automatic mode measures every candidate on this sheet, so the first run '
    + 'takes 20–30 seconds. Repeat runs are near-instant.';
}

/* ------------------------------------------------------------------ */
/* Dedup verification (rev 4 §1) — compare job_id, don't infer          */
/* ------------------------------------------------------------------ */

/**
 * Resolved mode per source, read from `sources[].prepress_mode`.
 *
 * This is the mode each file resolved to AFTER the broadcast/positional rule
 * was applied server-side. Reading it beats re-deriving the rule on our side:
 * our copy of the rule can drift, the server's answer cannot.
 *
 * @returns {Map<string, string|null>} source_id → mode
 */
export function resolvedModes(result) {
  const out = new Map();
  const sources = result?.sources;
  if (!Array.isArray(sources)) return out;
  for (const s of sources) {
    if (s && typeof s.source_id === 'string') {
      out.set(s.source_id, normalizeMode(s.prepress_mode));
    }
  }
  return out;
}

/** The mode a named source resolved to server-side, or null if untagged/unknown. */
export function resolvedModeFor(result, name) {
  const m = resolvedModes(result);
  return m.has(name) ? m.get(name) : null;
}

/* ------------------------------------------------------------------ */
/* Dieline previews (rev 5 §5, rev 6 §5.2) — PR ⑭b                     */
/* ------------------------------------------------------------------ */

/**
 * ⑭b DELETED `emittedGeometryFor()`, which found a carton's index in
 * `sources[]` and read `placements[].placed_vertices` at the matching
 * `type_index`.
 *
 * That mapping holds only while emitted pieces and uploaded sources are 1:1.
 * A tile breaks it: one source emits `<n>__pair` AND `<n>`, so every index
 * above it shifts by one and one carton's dieline renders under another's
 * name — on the card an operator checks before plating. It is invisible only
 * because `pair` is gated off, and that gate is a die-shop decision that can
 * land without a frontend release.
 *
 * `prepress_previews[]` is keyed by carton NAME and is a projection of the
 * report's `assets`, never independently computed geometry, so it cannot
 * disagree with what will be cut.
 */

/** One half of a preview — `before` or `after`. */
function previewFace(face) {
  if (!face || typeof face !== 'object') return null;
  const bbox = face.bbox_mm;
  const has2 = Array.isArray(bbox) && bbox.length === 2 && bbox.every(Number.isFinite);
  const verts = Array.isArray(face.vertices_mm) ? face.vertices_mm : null;
  return {
    vertices: verts,
    widthMm: has2 ? bbox[0] : null,
    heightMm: has2 ? bbox[1] : null,
    vertexCount: Number.isFinite(face.vertex_count) ? face.vertex_count : (verts ? verts.length : 0),
  };
}

/**
 * A named carton's dieline preview, or null.
 *
 * Null has ONE meaning worth acting on: this deployment's package predates
 * `assets` (<0.13.0), so previews are omitted rather than partial. Render
 * nothing. Do NOT synthesise geometry from the nesting result — that is the
 * lookup this PR removed.
 *
 * @returns {{ name, mode, before, after, tile, fingerprint }|null}
 */
export function prepressPreviewFor(result, name) {
  const list = result?.prepress_previews;
  if (!Array.isArray(list) || typeof name !== 'string') return null;
  const hit = list.find((p) => p?.name === name);
  if (!hit) return null;
  return {
    name: hit.name,
    mode: normalizeMode(hit.mode),
    before: previewFace(hit.before),
    after: previewFace(hit.after),
    tile: hit.tile ?? null,
    fingerprint: typeof hit.fingerprint === 'string' ? hit.fingerprint : null,
  };
}

/**
 * ONE viewport for both halves, so before and after render at a shared SCALE
 * and the operator can see how much the flaps actually add.
 *
 * `vertices_mm` are mm, y-up, origin bottom-left, anchored at each half's own
 * local origin — so the union box needs no offset and no normalisation:
 *
 *   [0, 0, max(before.w, after.w), max(before.h, after.h)]
 *
 * SIDE BY SIDE ONLY (rev 6 §5.2). Do not overlay these. Each half is anchored
 * at its OWN origin but they have different bboxes, because flaps extend past
 * the base outline — so drawing both at (0,0) mis-registers the base inside the
 * blank, and the result looks entirely plausible: flaps appearing to grow from
 * the wrong edges. The offset that would make an overlay honest is not
 * confirmed to be carried by `assets`, so it is not available to us.
 *
 * @returns {[number,number,number,number]|null}
 */
export function previewViewBox(preview) {
  const w = Math.max(preview?.before?.widthMm || 0, preview?.after?.widthMm || 0);
  const h = Math.max(preview?.before?.heightMm || 0, preview?.after?.heightMm || 0);
  if (!(w > 0) || !(h > 0)) return null;
  return [0, 0, w, h];
}

/** Both halves have drawable geometry and a shared frame to draw them in. */
export function previewIsRenderable(preview) {
  return Boolean(
    Array.isArray(preview?.before?.vertices) && preview.before.vertices.length >= 3
    && Array.isArray(preview?.after?.vertices) && preview.after.vertices.length >= 3
    && previewViewBox(preview),
  );
}

/**
 * A run's identity for dedup purposes: what was submitted — artwork, counts,
 * sheet, kerf, margins. How it was tagged is `prepressSignature`, kept separate
 * because only some tagging differences have to move the `job_id` (see
 * detectDedupCollision).
 */
export function runSignature(cartons, sheet, kerf, margins) {
  const names = (Array.isArray(cartons) ? cartons : [])
    .map((c) => `${sourceIdOf(c) || '?'}x${c?.count ?? 1}`).join('|');
  const m = margins || {};
  return JSON.stringify([names, sheet?.w, sheet?.h, kerf || 0,
    m.top || 0, m.right || 0, m.bottom || 0, m.left || 0]);
}

/**
 * WHICH cartons were tagged for prepress, and as which carton type — never the
 * mode. Positional, like the request: `null` for an untagged carton, its type
 * for a tagged one.
 *
 * An unset type signs as BL because that is what it means on the wire (an
 * omitted `?carton_type=` is BL), so unset and an explicit BL are the same die.
 * It must never sign as null, which would make a tagged carton look untagged.
 * The bench spells the field `cartonType`; stored refs use the wire key.
 */
export function prepressSignature(cartons) {
  if (!Array.isArray(cartons)) return JSON.stringify([]);
  return JSON.stringify(cartons.map((c) => {
    if (normalizeMode(c?.prepress) === null) return null;
    const t = c?.cartonType ?? c?.carton_type;
    return typeof t === 'string' && t ? t : DEFAULT_CARTON_TYPE;
  }));
}

/**
 * The dedup check rev 4 §1 asks for, replacing three heuristics with one:
 *
 *   same artwork + a different SET of tagged cartons, or a tagged carton cut
 *   as a different carton_type  =>  job_id MUST differ
 *
 * The job_id is a content hash of the payload AFTER `apply_prepress()` — names,
 * counts and vertices — so only a difference that guarantees different
 * geometry guarantees a different id. Two do:
 *
 *   - untagged vs tagged. An untagged carton passes its bare outline through
 *     (prepress_v1 api.py, `spec is None`); every tagged mode emits the flapped
 *     blank. Same id => the transform was skipped and the cache is serving the
 *     bare outline.
 *   - a tagged carton's type. The family decides which blank is built.
 *
 * The MODE is excluded on purpose. Between tagged modes an identical job_id is
 * legitimate: `auto` resolves to `plain` or `pair` and then nests exactly that
 * geometry, and `pair` at count 1 is `plain`. Keyed on mode, this check told an
 * operator not to plate a correct result when Hepaguard ran `plain` and then
 * `auto` and auto kept it plain (test box, 2026-09-26, job_743ed52b9ba2).
 * An untagged carton's type is excluded for the same reason: its outline passes
 * through untouched, so the type changes nothing that is hashed.
 *
 * Whether prepress ran for each tagged carton is `verifyPrepressRan`'s question,
 * answered positively from the response rather than inferred from history.
 *
 * @param {Array} history  prior runs: { signature, prepressSignature, jobId }
 * @param {{signature, prepressSignature, jobId}} current
 * @returns {{ collision: boolean, reason: string|null, priorJobId: string|null }}
 */
export function detectDedupCollision(history, current) {
  const none = { collision: false, reason: null, priorJobId: null };
  if (!Array.isArray(history) || !current?.jobId || !current?.signature) return none;
  const clash = history.find((h) => h
    && h.signature === current.signature
    && h.prepressSignature !== current.prepressSignature
    && h.jobId === current.jobId);
  if (!clash) return none;
  return {
    collision: true,
    priorJobId: clash.jobId,
    reason: 'This job reused the job id of an earlier run of the same artwork with '
      + 'different cartons tagged for prepress, or a different carton type. Prepress '
      + 'transforms the geometry before the job is hashed, so the two must differ — an identical id means the flapped dieline was not generated '
      + 'and a cached result was returned. Do not send this to plate; report it.',
  };
}

/* ------------------------------------------------------------------ */
/* Capabilities (rev 3 §5.1) — ask, don't discover by failing           */
/* ------------------------------------------------------------------ */

/**
 * Normalize `GET /planner/prepress/capabilities`.
 *
 * This supersedes discovering the tiling flag by walking into a 400. The
 * discovery path below is KEPT as a fallback for a backend that predates the
 * endpoint, but when capabilities has answered it wins — a probe that costs a
 * failed run is not a good first experience.
 *
 * @returns {{ available, modes, asyncOnlyModes, allowTiling, tilingQuotingOnly,
 *             packageVersion, cartonTypes, features, deprecated,
 *             tilingGateUnreliable, detail }}
 */
export function normalizeCapabilities(body) {
  const available = body?.available === true;
  const modes = Array.isArray(body?.modes)
    ? body.modes.map(normalizeMode).filter(Boolean) : [];
  const asyncOnly = Array.isArray(body?.async_only_modes)
    ? body.async_only_modes.map(normalizeMode).filter(Boolean) : [];
  const pkg = typeof body?.package_version === 'string' ? body.package_version : null;
  const features = (body?.features && typeof body.features === 'object' && !Array.isArray(body.features))
    ? body.features : {};
  const deprecated = Array.isArray(body?.deprecated) ? body.deprecated.filter((k) => typeof k === 'string') : [];
  return {
    available,
    modes,
    asyncOnlyModes: asyncOnly,
    allowTiling: body?.allow_tiling === true,
    tilingQuotingOnly: body?.tiling_is_quoting_only !== false,
    packageVersion: pkg,
    // ⑭a / rev 5 §8.1: what this deployment ACCEPTS. Drives the carton-type
    // picker instead of a hard-coded list, because it comes from the same
    // constant the validator checks — so it cannot offer a type that would be
    // rejected or omit one that would be accepted. Note this is "accepted",
    // NOT "signed off": §11's rollout discipline still applies per type, and
    // IL starts at step 2.
    cartonTypes: Array.isArray(body?.carton_types)
      ? body.carton_types.filter((t) => typeof t === 'string') : [],
    // feature name → the package version that introduced it. Use it to decide
    // whether to RENDER a feature at all.
    features,
    deprecated,
    // ⑭a: the tiling gate's reliability now reads off `features`, not the
    // deprecated `min_package_version` (rev 5 §8.2 — which was only ever
    // derived from features.allow_tiling anyway). A deployment whose package
    // predates the gate has no `features.allow_tiling` entry at all.
    tilingGateUnreliable: available && !features.allow_tiling,
    // ⑭e / rev 7 §5: the per-flap shape, MEASURED per deployment rather than
    // asserted by a constant. A hand-set flag would only ever have meant
    // "someone ran a probe once" — it goes stale on the next package upgrade
    // and nothing notices.
    flapShape: normalizeFlapShape(body?.shapes?.flap_regions),
    // ⑭k / rev 12 §1 + §5 — the FAMILY surface, published rather than
    // described. Every family question this client asks is answered from here
    // and never from a table of ours: which topologies may be tagged, which
    // dimension a family adjusts, and whether its flap shape was measured at
    // all. rev 12's own words: the accepted set has both widened and narrowed
    // in this project's life.
    unflappedVertexCounts: numberListMap(body?.unflapped_vertex_counts),
    flapDimensionInputs: stringListMap(body?.flap_dimension_inputs),
    // §5: `shapes.flap_regions` now carries `applies_to: ["BL"]` because it
    // always was the BL answer. Publishing it unqualified is what would tell
    // us a tuck carton has a `tf` slider.
    flapShapeByFamily: mapValues(body?.shapes?.by_family, normalizeFlapShape),
    familiesMeasured: strings(body?.shapes?.families_measured),
    familiesUnmeasured: normalizeUnmeasured(body?.shapes?.families_unmeasured),
    // ⑭l / rev 12 §5 — DISTINCT from families_unmeasured, and the one the
    // picker gates on. `carton_types` is what this BACKEND accepts; whether the
    // INSTALLED package supports each is measured separately, because the
    // package version cannot answer it — prepress contract v3.0 through v3.4
    // all ship as 0.17.0. Unmeasured means our repo has no reference outline;
    // unavailable means the deployment would refuse the type.
    familiesUnavailable: normalizeUnmeasured(body?.shapes?.families_unavailable),
    reportKeysDifferByFamily: (body?.shapes?.report_keys_differ_by_family
      && typeof body.shapes.report_keys_differ_by_family === 'object')
      ? body.shapes.report_keys_differ_by_family : null,
    detail: typeof body?.detail === 'string' ? body.detail : null,
    // Deployment switch for the editor's hand-built pair ARRANGEMENT.
    // Normalised like every other capability: App.js was reading the raw
    // snake_case key off this already-camelCased object, so it was always
    // `undefined` and the switch could never be turned ON — the backend
    // publishing `true` changed nothing. It read as "working" only because
    // undefined and false look the same at the call site.
    editorCustomPairArrangement: body?.editor_custom_pair_arrangement === true,
    // ⑭d follow-up / §6: WHICH COMPONENT evaluates a firm order line here.
    // Absent on a backend that predates the key, so it falls back to LEGACY —
    // see LEGACY_REQUIRED_OWNERSHIP for why that direction and not the other.
    requiredOwnership: normalizeRequiredOwnership(body?.required_ownership),
  };
}

/**
 * What a deployment that does not publish `required_ownership` must be assumed
 * to be.
 *
 * LEGACY, deliberately — the conservative direction is the one that assumes
 * FEWER states are honoured, because being wrong that way shows the operator a
 * reason they can act on, while being wrong the other way lets them mark a line
 * firm that is then silently stripped. `config.py` defaults
 * `prepress_required_passthrough` to False and `railway.json` sets no
 * environment, so an unpublishing backend is overwhelmingly likely to BE legacy
 * rather than merely old.
 */
export const LEGACY_REQUIRED_OWNERSHIP = Object.freeze({
  published: false,
  requiredPassthrough: false,
  owner: 'prepress',
  modesEvaluated: Object.freeze(['auto']),
  untaggedEvaluated: 'only_when_job_has_auto',
  inertIsPossible: true,
  inertErrorCode: 'RequiredRequiresAuto',
});

/** Read `required_ownership`, falling back to LEGACY when it is absent. */
export function normalizeRequiredOwnership(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return LEGACY_REQUIRED_OWNERSHIP;
  }
  const modes = strings(raw.modes_evaluated);
  const untagged = raw.untagged_evaluated === 'always'
    ? 'always' : 'only_when_job_has_auto';
  return Object.freeze({
    published: true,
    requiredPassthrough: raw.required_passthrough === true,
    owner: typeof raw.owner === 'string' && raw.owner ? raw.owner : 'prepress',
    // An empty list would silently mean "nothing is ever honoured" and hide the
    // control everywhere; a published key with no modes is malformed, not a
    // statement, so fall back rather than act on it.
    modesEvaluated: Object.freeze(modes.length ? modes : ['auto']),
    untaggedEvaluated: untagged,
    inertIsPossible: raw.inert_is_possible !== false,
    inertErrorCode: typeof raw.inert_error_code === 'string'
      ? raw.inert_error_code : 'RequiredRequiresAuto',
  });
}

/** The ownership rules in force, for a caps object that may be null. */
export function requiredOwnershipOf(caps) {
  return caps?.requiredOwnership || LEGACY_REQUIRED_OWNERSHIP;
}

/** Local list readers — prepress.js stays dependency-free so .cjs can mirror it. */
function strings(v) {
  return Array.isArray(v) ? v.filter((s) => typeof s === 'string' && s) : [];
}

function mapValues(obj, fn) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[k] = fn(v);
  return out;
}

function numberListMap(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const nums = Array.isArray(v) ? v.filter(Number.isFinite) : [];
    if (nums.length) out[k] = nums;
  }
  return Object.keys(out).length ? out : null;
}

function stringListMap(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const list = strings(v);
    if (list.length) out[k] = list;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * `shapes.families_unmeasured` — SHAPE UNCONFIRMED, read both ways.
 *
 * rev 12 §5 says each unmeasured entry "carries a reason", which means an
 * object; the only example it publishes is the empty list `[]`, which shows
 * nothing. ⑭f wrote copy from a code name and ⑭h inferred a join from
 * documentation examples; both cost a run. So this accepts a bare family
 * string and an object carrying one, and asks backend rather than picking.
 */
export function normalizeUnmeasured(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((e) => {
      if (typeof e === 'string' && e) return { family: e, reason: null };
      if (e && typeof e === 'object') {
        const family = [e.family, e.name, e.carton_type].find((v) => typeof v === 'string' && v);
        if (!family) return null;
        return { family, reason: typeof e.reason === 'string' ? e.reason : null };
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * The unflapped corner counts this deployment accepts for a family.
 *
 * Falls back to `{ BL: [6] }` and NOT to rev 12's full table — offering a tag
 * for a family the deployment has not published is offering one the server
 * would then reject.
 *
 * @returns {number[]|null}
 */
export function unflappedCountsFor(caps, family) {
  const published = caps?.unflappedVertexCounts;
  const key = (typeof family === 'string' && family) ? family : DEFAULT_CARTON_TYPE;
  if (published && Array.isArray(published[key])) return published[key];
  if (published) return null;                       // published, and not for this family
  return FALLBACK_UNFLAPPED_COUNTS[key] || null;
}

/** Every unflapped corner count this deployment accepts, across all families. */
export function acceptedUnflappedCounts(caps) {
  const published = caps?.unflappedVertexCounts || FALLBACK_UNFLAPPED_COUNTS;
  const all = new Set();
  for (const list of Object.values(published)) {
    for (const n of (Array.isArray(list) ? list : [])) if (Number.isFinite(n)) all.add(n);
  }
  return [...all].sort((a, b) => a - b);
}

/**
 * Which families take this corner count — for COPY only.
 *
 * Never for identification. rev 12 §1.3 is explicit that a family cannot be
 * read off a vertex count, and §2 says the halt message deliberately does not
 * name one for exactly this reason. Used to tell an operator what a count is
 * accepted as, never to decide what a carton is.
 */
export function familiesTakingCount(caps, n) {
  const published = caps?.unflappedVertexCounts || FALLBACK_UNFLAPPED_COUNTS;
  return Object.keys(published)
    .filter((f) => (published[f] || []).includes(n))
    .sort();
}

/**
 * The config dimensions a family adjusts, as FLAP keys (rev 12 §1, §4).
 *
 * `flap_dimension_inputs` publishes `["gf_mm","tuck_lip_mm"]`; the flap keys in
 * `flap_dims_mm` are `gf` and `tuck_lip`. Dropping the `_mm` suffix is a
 * mechanical strip of a published name, not a guess at one — the two
 * vocabularies this project keeps getting wrong are `gf`/`glue`, and that join
 * still goes through FLAP_ALIAS.
 *
 * @returns {string[]}  empty when this deployment publishes nothing for it
 */
export function adjustableFlapsFor(caps, family) {
  const published = caps?.flapDimensionInputs;
  const key = (typeof family === 'string' && family) ? family : DEFAULT_CARTON_TYPE;
  const inputs = published ? published[key] : null;
  if (Array.isArray(inputs)) return inputs.map((k) => k.replace(/_mm$/, '')).filter(Boolean);
  if (published) return [];
  return key === DEFAULT_CARTON_TYPE ? ['gf', 'tf'] : [];
}

/**
 * The flap-region shape measured for THIS family (rev 12 §5).
 *
 * `shapes.flap_regions` is the BL answer and now says so with `applies_to`.
 * Reading it for an RTE is how a `tf` slider would appear on a carton that has
 * no `tf`. An unqualified `flap_regions` (a deployment predating rev 12) is
 * read as the BL answer, which is what it was.
 */
export function flapShapeFor(caps, family) {
  const key = (typeof family === 'string' && family) ? family : DEFAULT_CARTON_TYPE;
  const byFamily = caps?.flapShapeByFamily;
  if (byFamily && byFamily[key]) return byFamily[key];
  const shape = caps?.flapShape;
  if (!shape) return null;
  const appliesTo = shape.appliesTo;
  if (appliesTo.length === 0) return key === DEFAULT_CARTON_TYPE ? shape : null;
  return appliesTo.includes(key) ? shape : null;
}

/**
 * The carton types this client may OFFER (rev 12 §5).
 *
 * `carton_types` minus `families_unavailable`. Advertising a type the installed
 * package would refuse is the same failure as a hard-coded list, one layer
 * further in — and the package version cannot be used to work it out, because
 * prepress contract v3.0 through v3.4 all ship as 0.17.0.
 */
export function offerableCartonTypes(caps) {
  const all = Array.isArray(caps?.cartonTypes) ? caps.cartonTypes : [];
  const off = new Set((caps?.familiesUnavailable || []).map((e) => e.family));
  return all.filter((t) => !off.has(t));
}

/** Why a type this deployment advertises cannot be offered, or null. */
export function cartonTypeUnavailableReason(caps, family) {
  const hit = (caps?.familiesUnavailable || []).find((e) => e.family === family);
  if (!hit) return null;
  return hit.reason || `This server's prepress package does not support ${family} cartons.`;
}

/**
 * The families whose published topology matches this outline — a SUGGESTION.
 *
 * The contract's own framing, in its own words: *"`suggested_carton_types` is
 * what the operator should be OFFERED, not what the backend applies.
 * carton_type is a manufacturing decision — which die gets cut — and the
 * backend does not infer it from geometry."*
 *
 * We do not infer it either. This orders and highlights a picker; it never
 * writes a value. The reason is spelled out in the contract and is worth
 * repeating here because the code looks like it could: among the types
 * supported TODAY the topologies are disjoint, so a mapping would work — and
 * IL shares BL's 6 vertices and its reinstatement trigger is still live, so
 * that mapping would acquire a silently wrong branch the day IL returns.
 */
export function suggestedCartonTypes(caps, polygon) {
  const n = outlineCornerCount(polygon);
  if (n === null) return [];
  return offerableCartonTypes(caps).filter((t) => (unflappedCountsFor(caps, t) || []).includes(n));
}

/**
 * The type a carton will actually be cut as: the chosen one, or the wire
 * default when nothing was chosen (an omitted `?carton_type=` means BL).
 */
export function effectiveCartonType(carton, caps) {
  const t = carton?.cartonType;
  if (typeof t === 'string' && t) return t;
  const offerable = offerableCartonTypes(caps);
  return offerable.includes(DEFAULT_CARTON_TYPE) ? DEFAULT_CARTON_TYPE : null;
}

/**
 * Our copy of the refusal the backend now issues up front — 400
 * `CartonTypeTopologyMismatch`.
 *
 * ⑭l. This is what the STE_200 ML KEY LINE job hit: no picker existed, so
 * `carton_type` was never sent, an omitted parameter means BL, and an 8-vertex
 * outline is not a BL base. The backend used to hand that to the package, which
 * answered *"BL base must be exactly 6 (x, y) vertices"* — accurate about the
 * consequence and naming the wrong family.
 *
 * Checked here so the operator is stopped at the rail with the fix in front of
 * them instead of after an upload. AFFORDANCE still: the 400 is authoritative
 * and this never picks a type, it only refuses to proceed with one that cannot
 * be right and says which are on offer.
 *
 * TWO rules now, in this order:
 *
 *   1. the type in play cannot cut this outline (the ⑭l refusal above), and
 *   2. a TAGGED carton carries no explicit type at all.
 *
 * Rule 2 was added because rule 1 turned out to be an incidental guard rather
 * than a structural one — it only fires when the vertex count contradicts the
 * BL default, so a tagged 6-corner carton with an untouched picker sailed
 * through and shipped as BL. See the comment at the rule itself.
 *
 * Returns null for an untagged carton — prepress does not run, so no die
 * family is being claimed and there is nothing to pick. That is also why a
 * null slot in `?carton_type=` is legitimate rather than a defect to gate on.
 */
export function cartonTypeBlockReason(carton, caps) {
  if (!carton?.prepress) return null;
  const chosen = typeof carton.cartonType === 'string' && carton.cartonType ? carton.cartonType : null;
  const offerable = offerableCartonTypes(caps);
  const n = outlineCornerCount(carton?.detection?.vertices);
  const used = effectiveCartonType(carton, caps);
  const counts = used ? unflappedCountsFor(caps, used) : null;

  // ── 1. The type IN PLAY cannot cut this outline. ──────────────────────
  // Checked first because it is the more specific answer: it can name the
  // family that DOES take this count, which "pick something" cannot.
  if (n !== null && used && counts && counts.length > 0 && !counts.includes(n)) {
    const suggested = suggestedCartonTypes(caps, carton.detection.vertices);
    const head = chosen
      ? `This outline has ${n} corners, which is not the unflapped topology of ${used} (${counts.join(' or ')}).`
      : `No carton type is set, so this would be cut as ${used} — but a ${n}-corner outline is not a ${used} base (${used} takes ${counts.join(' or ')}).`;
    if (suggested.length) return `${head} ${suggested.join(' or ')} takes ${n}. Set the carton type on this row.`;
    // Nothing OFFERABLE takes this count. Two very different reasons, and the
    // operator can act on only one of them: a family this deployment advertises
    // but whose installed package refused it (§5 `families_unavailable`) is not
    // their drawing's fault, and telling them to remove the tag would be wrong.
    const blocked = (caps?.cartonTypes || [])
      .filter((t) => !offerable.includes(t))
      .filter((t) => (unflappedCountsFor(caps, t) || []).includes(n));
    if (blocked.length) {
      return `${head} ${blocked.join(' or ')} takes ${n}, but `
        + `${cartonTypeUnavailableReason(caps, blocked[0])}`;
    }
    return `${head} No carton type this server offers takes that count — if the flaps are already drawn, remove the prepress tag.`;
  }

  // ── 2. A TAGGED carton must carry an EXPLICIT type. ───────────────────
  //
  // The wire default (omitted means BL) is a COMPATIBILITY rule, not an
  // operator affordance, and rule 1 above is not a substitute for this one:
  // it only catches a missing type when the vertex count happens to
  // contradict BL. That safety is INCIDENTAL — it holds only while the
  // offered families have disjoint topologies. `suggestedCartonTypes` says
  // why that expires: IL shares BL's six corners and its reinstatement
  // trigger is live, and BL-vs-IL is which lock the die uses, which no
  // geometry check can ever decide. A silently defaulted BL there is the
  // wrong die cut with no warning at all.
  //
  // It is also a live trap without IL. The picker renders on every tagged
  // carton now that three families are offered, and it renders EMPTY — an
  // operator reads an untouched "carton type…" as "nothing will happen",
  // not as "this ships as BL".
  //
  // Gated on there being a choice to make. With one offerable type no picker
  // is rendered at all (see the `offerableTypes.length > 1` guard on the
  // control), so blocking would disable Run layout with no control on screen
  // that could clear it. Same reason an empty `offerable` — capabilities not
  // fetched yet, or the fetch failed — publishes no opinion rather than
  // killing the bench.
  if (chosen === null && offerable.length > 1) {
    const suggested = n === null
      ? [] : suggestedCartonTypes(caps, carton?.detection?.vertices);
    if (suggested.length) {
      return `No carton type is set. A ${n}-corner outline is the `
        + `${suggested.join(' or ')} topology, but which die gets cut is a `
        + `manufacturing decision — set the carton type on this row.`;
    }
    return `No carton type is set. Set the carton type on this row `
      + `(${offerable.join(', ')}).`;
  }
  return null;
}

/** Cartons that carry no carton type, or one their outline cannot take. */
export function cartonTypeBlock(cartons, caps) {
  const bad = (Array.isArray(cartons) ? cartons : [])
    .filter((c) => cartonTypeBlockReason(c, caps) !== null);
  if (bad.length === 0) return null;
  // Split by the ACTION, not by which rule fired: a carton with no type needs
  // one picked whether or not its topology also disagrees, and a carton that
  // has one needs it corrected. One banner naming both would send half the
  // bench to the wrong control.
  const label = (c) => sourceIdOf(c) || 'a carton';
  const isSet = (c) => typeof c.cartonType === 'string' && !!c.cartonType;
  const unset = bad.filter((c) => !isSet(c)).map(label);
  const wrong = bad.filter(isSet).map(label);
  if (bad.length === 1) {
    return unset.length
      ? `${unset[0]}: no carton type is set.`
      : `${wrong[0]}: carton type does not match the outline.`;
  }
  const parts = [];
  if (unset.length) parts.push(`No carton type set on: ${unset.join(', ')}.`);
  if (wrong.length) {
    parts.push(`Carton type does not match the outline on: ${wrong.join(', ')}.`);
  }
  return parts.join(' ');
}

/** Whether this deployment has a reference outline for this family (rev 12 §5). */
export function familyIsMeasured(caps, family) {
  const key = (typeof family === 'string' && family) ? family : DEFAULT_CARTON_TYPE;
  const unmeasured = caps?.familiesUnmeasured || [];
  if (unmeasured.some((e) => e.family === key)) return false;
  const measured = caps?.familiesMeasured || [];
  if (measured.length) return measured.includes(key);
  return null;                                       // this deployment says nothing
}

/**
 * `capabilities.shapes.flap_regions` — what this deployment's package actually
 * emits inside a flap region.
 *
 * Backend shipped this with a defect they found and fixed while writing rev 7:
 * the probe assumed a flap-keyed map, the package emits `{policy, note,
 * regions[]}`, and against a real deployment it would have reported
 * `measured: true` with an EMPTY `fields` — so `search_mm` could never appear
 * and we would have read their parsing miss as a package limitation. Hence
 * `container`: "unrecognised" is a finding, not a silence.
 */
export function normalizeFlapShape(shape) {
  if (!shape || typeof shape !== 'object' || Array.isArray(shape)) {
    return {
      present: false, measured: false, container: null,
      fields: [], flaps: [], containerValues: [], appliesTo: [],
    };
  }
  return {
    present: shape.present === true,
    measured: shape.measured === true,
    container: typeof shape.container === 'string' ? shape.container : null,
    fields: strings(shape.fields),
    flaps: strings(shape.flaps),
    // ⑭h / rev 8 §5: the COMPLETE set of container values, published rather
    // than described, with a backend test pinning it to what the extractor can
    // actually return. So our read-only fallback is now provably exhaustive
    // instead of merely cautious — and a value outside this set means the
    // deployment is ahead of this client, which is worth saying differently
    // from "this server cannot read its flaps".
    containerValues: strings(shape.container_values),
    // ⑭k / rev 12 §5: which families this measurement is about. Empty means
    // unqualified, which is how every deployment before rev 12 published it —
    // and it was the BL answer then too.
    appliesTo: strings(shape.applies_to),
  };
}

/**
 * Whether a flap slider may be bound with confidence (rev 7 §5).
 *
 * The rule is one line and the whole editor hangs off it: bind when
 * `search_mm` appears in `fields`. Anything else — unmeasured, an
 * unrecognised container, or a package that simply does not emit it — means we
 * do NOT know the searchable range, and rev 6 §4 is explicit that binding to
 * `range_mm` instead is worse than binding to nothing: the real Ginkoline glue
 * flap is `range_mm [12,17]` but `search_mm [12,12]`, locked, so that slider
 * offers an adjustment the engine refuses every single time.
 */
export function flapSlidersBindable(caps, family) {
  // ⑭k: read the shape measured for THIS family. A tuck carton must not
  // inherit BL's measurement — rev 12 §5 qualified `flap_regions` with
  // `applies_to` precisely because publishing it unqualified would promise a
  // `tf` slider on a carton that has no `tf`.
  const s = flapShapeFor(caps, family);
  return Boolean(s && s.present && s.measured
    && s.container !== 'unrecognised' && s.fields.includes('search_mm'));
}

/**
 * Why the flap rows are read-only, in words an operator can act on. Null when
 * they are bindable.
 */
export function flapSliderBlockReason(caps, family) {
  if (flapSlidersBindable(caps, family)) return null;
  const s = flapShapeFor(caps, family);
  // ⑭k / rev 12 §5: a family with no reference outline on the deployment is a
  // different answer from a server that cannot read its flaps, and it carries
  // its own reason. Say which one it is rather than blaming the server.
  if (!s) {
    const fam = (typeof family === 'string' && family) ? family : DEFAULT_CARTON_TYPE;
    const entry = (caps?.familiesUnmeasured || []).find((e) => e.family === fam);
    if (entry) {
      return entry.reason
        || `This server has not measured flap ranges for ${fam} cartons, so they are shown but not adjustable.`;
    }
    return `This server does not report flap ranges for ${fam} cartons, so they are shown but not adjustable.`;
  }
  if (!s.present) return 'This server does not report adjustable flap ranges.';
  if (s.container && s.containerValues.length && !s.containerValues.includes(s.container)) {
    return 'This server reports a flap format this page does not know yet. '
      + 'Ranges are shown but not adjustable — reload, and tell the team if it persists.';
  }
  if (!s.measured || s.container === 'unrecognised') {
    return 'This server could not read its own flap ranges, so they are shown but not adjustable.';
  }
  return 'This server does not publish a searchable flap range, so these are shown but not adjustable.';
}

/** Whether this deployment has a named feature at all (rev 5 §8.1). */
export function hasFeature(caps, name) {
  return Boolean(caps?.features && caps.features[name]);
}

/** Numeric-segment version compare; ignores any pre-release suffix. */
export function compareVersions(a, b) {
  const seg = (v) => String(v).split('-')[0].split('.').map((n) => parseInt(n, 10) || 0);
  const x = seg(a), y = seg(b);
  for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/**
 * Modes the UI may offer, preferring the server's answer over our constant.
 * `pair` is filtered out regardless until the quoting-only labelling ships.
 */
export function offerableModes(caps) {
  if (caps && caps.available && caps.modes.length) {
    return caps.modes.filter((m) => m !== 'pair');
  }
  return PREPRESS_UI_MODES.slice();
}

/* ------------------------------------------------------------------ */
/* Config (rev 3 §5.3) — prepress_config query parameter               */
/* ------------------------------------------------------------------ */

/**
 * Drop keys the operator did not set.
 *
 * `additionalProperties: false` server-side, so an unknown key is a 400
 * ConfigError rather than a silent ignore — send only what was actually set.
 *
 * @returns {object|null}  null when nothing survives
 */
export function cleanPrepressConfig(config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
  const clean = {};
  for (const [k, v] of Object.entries(config)) {
    if (v === undefined || v === null || v === '') continue;
    // ⑭h, last line of defence. A missed lookup stringifies to the KEY
    // "undefined", which is a perfectly valid JSON key and reached the server
    // as `{"undefined":[10.5,12]}` — a 400 on the first slider drag. The row
    // gate upstream is the real fix; this makes the failure unconstructible
    // rather than merely unlikely.
    if (typeof k !== 'string' || !k || k === 'undefined' || k === 'null') continue;
    // ⑭k / rev 12 §6: the tiling gate is a CLAMP, not a config tier — it is a
    // manufacturing decision, not a setting. The package now has per-family
    // switches (allow_tiling_rte and siblings) and an explicit `true` on any of
    // them is 400 TilingDisabled rather than accepted-and-ignored.
    //
    // Stripped rather than refused, for ⑭j's D7 reason: the right outcome is
    // the request the backend accepts, not a client-side error an operator
    // cannot act on. The editor cannot produce one of these — it only writes
    // keys the server confirmed — but `ui_state` is opaque, restores whatever
    // it was given, and `sanitizePresets` already drops engine-policy keys on
    // read for exactly this reason. One 400 per Run Layout, from a blob nobody
    // can see, is not a failure anyone would diagnose.
    if (isTilingSwitch(k)) continue;
    clean[k] = v;
  }
  return Object.keys(clean).length ? clean : null;
}

/** `allow_tiling` and every per-family sibling of it (rev 12 §6). */
export function isTilingSwitch(key) {
  return typeof key === 'string' && /^allow_tiling(_[a-z0-9_]+)?$/i.test(key);
}

/** The tiling switches present in a config — so a test can prove a strip bit. */
export function tilingSwitchOffenders(config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return [];
  return Object.keys(config).filter(isTilingSwitch);
}

/**
 * Serialize per-carton config for the export path — the POSITIONAL LIST form
 * (rev 5 §4.2, rev 7 §3).
 *
 * ```
 * ?prepress_config=[{"gf_range_mm":[10,14]}, null]
 * ```
 *
 * The list is positional and its length MUST equal the file count; a wrong
 * length is `400 PrepressSpecError`. So it is built from the same iteration
 * that appends `count` and `files`, never assembled separately.
 *
 * ⑭e / D3: we emit the list form and ONLY the list form. The object form is
 * the JOB tier, and a job tier is a different statement from "every carton
 * happens to agree" — collapsing the two when configs match would put the
 * job-vs-carton confusion back on the wire, which is the confusion rev 5 §4
 * exists to end. Returns null when no carton has any config, so an ordinary
 * job's content hash is unchanged.
 */
export function serializePrepressConfigList(configs) {
  if (!Array.isArray(configs) || configs.length === 0) return null;
  const cleaned = configs.map(cleanPrepressConfig);
  if (!cleaned.some((c) => c !== null)) return null;
  return JSON.stringify(cleaned);
}

/**
 * Serialize `?carton_type=` (rev 7 §2) — same broadcast/positional rule as the
 * rest of the family: a scalar applies to every file, a list is positional.
 *
 * ⑭e / D6: while the deployment accepts exactly one type, this returns null
 * and the parameter is omitted. Rev 7 §2 says omitted means `BL`, so sending
 * `carton_type=BL` explicitly would add a parameter to every job that did not
 * carry one before — changing the content hash and re-nesting on deploy day
 * for no gain. That is rev 7 §9's `allowed_rotations` lesson, and we paid for
 * it once already in ⑬h.
 *
 * The plumbing stays wired regardless, so reinstating `IL` after probe V11 is
 * a UI change here and nothing at all on the wire.
 */
export function serializeCartonType(types, offered) {
  const list = Array.isArray(offered) ? offered.filter((t) => typeof t === 'string') : [];
  if (list.length < 2) return null;           // nothing to distinguish — omit
  const vals = (Array.isArray(types) ? types : []).map((t) => (typeof t === 'string' && t ? t : null));
  if (!vals.some((t) => t !== null)) return null;
  const set = new Set(vals.filter(Boolean));
  if (set.size === 1 && vals.every((t) => t !== null)) return vals[0];   // scalar broadcasts
  return JSON.stringify(vals);
}

/* ------------------------------------------------------------------ */
/* ⑭e — POST /planner/prepress/describe (rev 6 §3)                     */
/* ------------------------------------------------------------------ */

/**
 * The body for a pre-run describe.
 *
 * Rev 6 §3 exists because of a hole our own D5 note exposed: `flap_regions`
 * comes from the REPORT, i.e. after a run, but config is set BEFORE one, so a
 * pre-run editor had nothing to bind to. `describe` nests nothing and needs no
 * sheet — one carton, `plain`, sub-second.
 *
 * `carton_type` is sent only when this deployment offers a choice, for the
 * same reason `?carton_type=` is omitted on the export path (D6).
 */
export function describeRequest({ vertices, config, cartonType, offeredTypes, name = null }) {
  const verts = Array.isArray(vertices)
    ? vertices.filter((p) => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite))
    : [];
  if (verts.length === 0) return null;
  const body = { vertices: verts };
  // THE JOIN KEY, when the answer is going to be KEPT rather than read once.
  // Backend names an unnamed describe `"carton"`, which is harmless for the
  // config editor (it reads flap ranges off the entry and throws it away) and
  // wrong for the editor's desk, which merges the entry into a prepress report
  // that `cartonTypeFor` and the artwork chain look up BY NAME. An entry filed
  // under "carton" is an entry no placement can ever find.
  if (typeof name === 'string' && name) body.name = name;
  const cfg = cleanPrepressConfig(config);
  if (cfg) body.config = cfg;
  // ⑭l — THIS GATE WAS `offered.length > 1`, AND IT WAS A PROXY FOR THE WRONG
  // THING. It meant "send a type only when the deployment offers a choice",
  // which was true while `carton_types` was `['BL']`. It is now three, so the
  // gate opens — but the condition that actually matters is whether we HAVE a
  // type, and the proxy never checked that. A described STE carton with no
  // type sent nothing, describe defaulted to BL, and an 8-vertex outline is not
  // a BL base. Send it when we have one and the deployment offers it; send
  // nothing when we do not, which is what an omitted parameter already means.
  const offered = Array.isArray(offeredTypes) ? offeredTypes : [];
  if (typeof cartonType === 'string' && cartonType && offered.includes(cartonType)) {
    body.carton_type = cartonType;
  }
  return body;
}

/**
 * Read a describe response.
 *
 * Backend returns the carton's report entry SPREAD, not nested, so whatever
 * the package adds flows through without a code change — which means we read
 * the same keys the report reader already knows, plus `preview`.
 *
 * Two things it deliberately never carries: `counts` and `selection`. Nothing
 * was measured against a sheet, so any number there would be invented, and a
 * yield figure inside a config editor gets read as a prediction. If either ever
 * appears we ignore it rather than render it.
 */
export function parseDescribe(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  return {
    entry: body,
    config: (body.config && typeof body.config === 'object' && !Array.isArray(body.config))
      ? body.config : null,
    preview: (body.preview && typeof body.preview === 'object') ? body.preview : null,
    flaps: flapRegionRows(body),
  };
}

/**
 * The editor's flap rows, read off `flap_regions`.
 *
 * `flap_regions` arrives in either of two containers — a flap-keyed map, or
 * `{policy, note, regions[]}` with each entry naming its own flap. Backend hit
 * this exact ambiguity in their own probe (rev 7 §5) and now accepts both; so
 * do we, because which one arrives is a property of the installed package.
 *
 * **Bound to `search_mm`, never `range_mm`** (rev 6 §4). `range_mm` is the
 * LEGAL range; `search_mm` is what the engine will actually search. They differ
 * often and a slider drawn from the legal range offers an adjustment that is
 * refused every time.
 *
 * `dust` and `bf` are DERIVED — pure formulas off the wall they hang from —
 * and have no adjustable range at all, so they are never editable regardless
 * of what fields arrive.
 */
export const DERIVED_FLAPS = ['dust', 'bf'];

/**
 * THE ONE JOIN between the two flap vocabularies. ⑭i.
 *
 * This project has two names for the same flap and they are used in different
 * blocks of the SAME report entry:
 *
 *   `flap_dims_mm`   short   gf · tf · dust · bf      ← what flapDims reads
 *   `flap_regions`   long    glue · tuck · dust · bottom  ← what the editor reads
 *   config keys      short   gf_range_mm · tf_range_mm
 *   the contract     short   derived_flaps_are_read_only: ["dust","bf"]
 *
 * ⑭h fixed the config-key half of this and left the other half. The result was
 * visible on a live run: the editor showed rows labelled `glue`, `bottom` and
 * `tuck`, and told the operator the server "did not report a setting" for the
 * BOTTOM flap — which is not a server problem at all, it is a derived formula.
 * Wrong diagnosis, right refusal.
 *
 * So the join lives in ONE table now and everything that has to cross between
 * the vocabularies goes through it: the config key, the derived test, and the
 * label. Adding a flap means adding one line here.
 *
 * STILL INFERENCE, still guarded. The mapping is published nowhere: rev 7 §5
 * lists flap names under `capabilities.shapes.flap_regions.flaps`, the config
 * keys come from `GET /planner/prepress/config-schema`, and nothing joins them.
 * `contract.test.js` asserts the gap is still there, so the day backend
 * publishes the join this table gets deleted. Until then the guard is the same
 * one ⑭h built: an alias may only ever make a row MORE read-only or better
 * labelled. It can never make one editable — that still requires the server to
 * acknowledge the key in its own resolved config.
 */
export const FLAP_ALIAS = { glue: 'gf', tuck: 'tf', bottom: 'bf' };

/** A flap's canonical SHORT name — the vocabulary the contract speaks. */
export function canonicalFlap(key) {
  if (typeof key !== 'string') return null;
  const k = key.trim().toLowerCase();
  if (!k) return null;
  return FLAP_ALIAS[k] || k;
}

/**
 * Flap key → the config key that adjusts its range, in either vocabulary.
 *
 * ⑭h — THIS TABLE IS WHY THERE WAS A BUG. The editor previously mapped
 * `gf`/`tf` because that is what the CONFIG keys look like (`gf_range_mm`).
 * The deployed package names the FLAPS `glue`/`tuck`, so the lookup missed and
 * the editor sent `{"undefined": [10.5, 12]}` — a live 400 on the first slider
 * drag. Two naming conventions, one of them inferred from examples.
 *
 * ⑭i: DERIVED from FLAP_ALIAS rather than listed, so the long and short forms
 * cannot disagree about which config key a flap owns. A key it produces is
 * still used only after `flapRegionRows` finds it in the server's own resolved
 * config. Proposing is ours; disposing is the server's.
 */
export const FLAP_CONFIG_KEY = ['gf', 'tf'].reduce((acc, short) => {
  acc[short] = `${short}_range_mm`;
  Object.keys(FLAP_ALIAS)
    .filter((long) => FLAP_ALIAS[long] === short)
    .forEach((long) => { acc[long] = `${short}_range_mm`; });
  return acc;
}, {});

export function flapRegionRows(entry) {
  const fr = entry?.flap_regions;
  if (!fr || typeof fr !== 'object') return [];
  // The run's FULLY RESOLVED config — describe returns it, and so does
  // prepress_report.cartons[]. It is the only published list of key names this
  // deployment actually accepts, so it is what a proposed key is checked against.
  const resolved = (entry.config && typeof entry.config === 'object' && !Array.isArray(entry.config))
    ? entry.config : null;

  let items = [];
  if (Array.isArray(fr.regions)) {
    items = fr.regions
      .filter((r) => r && typeof r === 'object')
      .map((r) => [r.flap || r.name || r.key, r]);
  } else if (!Array.isArray(fr)) {
    items = Object.entries(fr).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v));
  }

  const pair = (v) => (Array.isArray(v) && v.length === 2 && v.every(Number.isFinite)
    ? [v[0], v[1]] : null);

  // ⑭i: two dust regions are normal (region order is glue · bottom · tuck ·
  // dust · dust), so `key` is NOT unique and cannot be a React key. Each row
  // carries a rowId that counts repeats — the editor keyed on `key` and React
  // warned on every render of every open config overlay.
  const seen = Object.create(null);

  return items
    .filter(([key]) => typeof key === 'string' && key)
    .map(([key, r]) => {
      const search = pair(r.search_mm);
      const legal = pair(r.range_mm);
      // ⑭i: through the ONE join table, so `bottom` reads as the derived flap
      // it is instead of falling through to "this server did not report a
      // setting for this flap" — which named the wrong culprit on a live run.
      const canon = canonicalFlap(key);
      const derived = DERIVED_FLAPS.includes(canon);
      seen[key] = (seen[key] || 0) + 1;
      const rowId = seen[key] > 1 ? `${key}#${seen[key]}` : key;
      // `locked`/`locked_reason` were unmeasured as of rev 6 §5.1 and are read
      // defensively still — rev 7 §5 moved the measurement to
      // `capabilities.shapes`, which reports FIELD NAMES, not whether a given
      // flap is locked on this carton.
      const locked = r.locked === true || (search !== null && search[0] === search[1]);
      // ⑭h: the key this row would write, resolved and then CONFIRMED against
      // the run's own resolved config. A row whose key the server does not
      // acknowledge is not adjustable — so the editor cannot construct a key
      // the server will reject, which is exactly what it did on the first
      // slider drag before this existed.
      const proposed = FLAP_CONFIG_KEY[key] || null;
      const configKey = (proposed && resolved && proposed in resolved) ? proposed : null;
      return {
        key,
        // ⑭i: unique per row. `key` is not — see `seen` above.
        rowId,
        canon,
        label: FLAP_LABELS[canon] || FLAP_LABELS[key] || key,
        derived,
        configKey,
        search,
        legal,
        currentMm: Number.isFinite(r.current_mm) ? r.current_mm
          : (Number.isFinite(r.default_mm) ? r.default_mm : null),
        locked,
        lockedReason: typeof r.locked_reason === 'string' ? r.locked_reason : null,
        // Adjustable only where the engine will actually honour a change: not
        // derived, not locked, a real searchable span to move within, AND a
        // config key the server has confirmed it knows.
        adjustable: !derived && !locked && search !== null && search[0] !== search[1]
          && configKey !== null,
      };
    });
}

const FLAP_LABELS = { gf: 'Glue flap', tf: 'Tuck flap', dust: 'Dust flap', bf: 'Bottom flap' };

/**
 * Whether a row may be dragged, given what this deployment measured.
 *
 * Two independent gates and BOTH must hold: the row itself must be adjustable
 * (not derived, not locked, a real span), and the deployment must have
 * confirmed it emits `search_mm` at all. Failing the second while showing a
 * slider would mean guessing at the field the engine reads.
 */
export function flapRowEditable(row, caps, family) {
  return Boolean(row?.adjustable && flapSlidersBindable(caps, family));
}

/**
 * Editor rows bound to a BASELINE description, with a LIVE one layered on top
 * for display only. ⑭i.
 *
 * THE BUG THIS EXISTS TO END
 * --------------------------
 * `describe` is called with the operator's config, and the response's
 * `search_mm` is a FUNCTION OF THAT CONFIG — sending `tf_range_mm: [17.5, 20]`
 * overrides the legal range, and reduction-only then reports the search span as
 * [17.5, 20]. The editor was binding the slider's own `min`/`max` to that
 * response, so every drag rewrote the control's own floor:
 *
 *     min=15    value=15      drag to 17.5
 *     min=17.5  value=17.5    ← thumb snaps to the left edge
 *     min=19.5  value=19.5    ← again; and a drag back down to 16 is CLAMPED
 *                               to 19.5 by the browser and does nothing at all
 *
 * A ratchet. The operator could raise a flap's floor and never lower it, the
 * travel shrank on every drag, and the only way back was Reset to defaults.
 * Observed on a live run before it was reproduced here.
 *
 * THE RULE, STATED ONCE
 * ---------------------
 * **Everything that decides whether and how far a control may move comes from
 * the BASELINE — the carton described with no config of ours. The live entry
 * contributes only text and the preview.** Bounds, the lock, derivedness and
 * `adjustable` are properties of the CARTON; a span narrowed by our own request
 * is not a measurement, it is an echo. Reading policy back out of an echo is
 * how the control moved under the operator's hand.
 *
 * Matching is by `rowId`, not by `key`: two dust regions are normal.
 *
 * @param {Object} baseline  describe response with NO config sent
 * @param {Object} [live]    describe response with the operator's config
 */
export function bindFlapRows(baseline, live) {
  const base = flapRegionRows(baseline);
  if (!base.length) return [];
  const byId = new Map(flapRegionRows(live).map((r) => [r.rowId, r]));
  return base.map((b) => {
    const l = byId.get(b.rowId) || null;
    return {
      ...b,
      // The slider's own extent. Never re-read from a response our edit caused.
      bounds: b.search,
      // What the engine says it will search UNDER THE CURRENT CONFIG. Display
      // only — it is the echo, and it is useful precisely because it shows the
      // operator the effect of what they set.
      resolved: l ? l.search : null,
      currentMm: (l && l.currentMm !== null && l.currentMm !== undefined)
        ? l.currentMm : b.currentMm,
    };
  });
}

/** Only `x-exposure: job` keys belong in an operator form (rev 3 §5.2). */
export function jobExposureKeys(schema) {
  const props = schema?.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.entries(props)
    .filter(([, v]) => v && v['x-exposure'] === 'job')
    .map(([k]) => k);
}

/* ------------------------------------------------------------------ */
/* Duplicate tagged names (rev 3 §8.6.4)                               */
/* ------------------------------------------------------------------ */

/**
 * Names shared by two or more TAGGED cartons.
 *
 * Report entries correlate by name, so a collision would attach one carton's
 * dieline to another. The backend now rejects this with 400 DuplicateCartonName;
 * catching it client-side saves the round trip, because our uploader can
 * absolutely produce two `Hepaguard.pdf` from different folders.
 *
 * Untagged cartons are unaffected — duplicates there are legitimate.
 */
export function duplicateTaggedNames(cartons) {
  const names = taggedCartonNames(cartons);
  const seen = new Set(), dupes = new Set();
  for (const n of names) {
    if (seen.has(n)) dupes.add(n); else seen.add(n);
  }
  return [...dupes];
}

/** Operator copy for a client-side duplicate, or null when there is none. */
export function duplicateNameBlockReason(cartons) {
  const d = duplicateTaggedNames(cartons);
  if (d.length === 0) return null;
  return `Two tagged cartons are both named ${d.join(', ')}. `
    + 'Prepress matches its report to cartons by name, so rename one before running — '
    + "otherwise one carton's dieline could be attached to the other.";
}

/* ------------------------------------------------------------------ */
/* Errors (reply §2.1)                                                 */
/* ------------------------------------------------------------------ */

/**
 * Operator copy per stable error code.
 *
 * Two families: exception class names preserved verbatim from prepress_v1,
 * and the backend's own guards. `code` is stable and safe to switch on.
 */
export const PREPRESS_ERROR_COPY = {
  // ── from prepress_v1 (contract §7) ──
  InputContractError: {
    title: "This outline isn't a valid carton base",
    hint: 'It must be a single 6-corner L with no flaps. A finished dieline will be rejected — prepress adds the flaps itself.',
  },
  PoseError: {
    title: 'This drawing is a mirror image',
    hint: "Redraw it un-mirrored, or clear 'artwork crosses creases'.",
  },
  DegenerateError: {
    title: "These panel widths can't fold into a box",
    hint: 'The front and back panels exceed half the body width. Check the structural drawing.',
  },
  ToleranceError: {
    title: 'The drawing is off by more than 0.02 mm',
    hint: 'Re-export or repair the dieline and try again.',
  },
  SlabDomainError: {
    title: 'Flap size is out of range',
    hint: 'Bring the glue and tuck flap sizes back inside their allowed range.',
  },
  EnginePolicyNotAccepted: {
    // ⑭h / rev 8 §7. We no longer send either refused parameter, so in a fresh
    // client this is unreachable — but a browser tab left open across the
    // deploy is running the PREVIOUS build, which does send them, and every
    // layout it runs will 400 until it reloads. That tab is the whole reason
    // this entry exists.
    title: 'This page is out of date',
    hint: 'Reload the page and run the layout again. Rotation and time-limit policy '
      + 'are now set on the server for this deployment.',
  },
  PassthroughNotClientSettable: {
    // ⑭j / rev 11 §6.5. Written from the PUBLISHED meaning, not from the
    // code's name — ⑭f's ConfigError copy was invented from a name and sent
    // operators to the wrong control for a year.
    //
    // `required_passthrough` decides WHICH COMPONENT evaluates firm order
    // lines, and the two measure different runs. That is a deployment
    // decision, so there is no operator action here beyond reloading: we do
    // not send it, and a client that does is a stale tab.
    title: 'This page is out of date',
    hint: 'Reload the page and try again. Whether firm order lines are checked by '
      + 'prepress or by the engine is set on the server for this deployment.',
  },
  EngineConfigNotSettablePerOrg: {
    // Contract 1.7 / K5. A control-centre code: it comes from
    // PATCH /admin/orgs/{id}/engine-config, which this client never calls. Copy
    // exists because the contract diff requires every published code to have
    // some, and it routes to "report this" like TraceNotFound — an operator
    // seeing it here means we called something we should not have.
    title: 'Something went wrong on our side',
    hint: 'A server setting was changed in a way the server does not allow. Nothing '
      + 'is wrong with your layout — please report it.',
  },
  // ── the engine boundary (backend Phase 2) ──────────────────────────────
  // These four arrived when the engine became its own service. Each entry is
  // written from the contract's PUBLISHED meaning, never from the code's name
  // — ⑭f's ConfigError copy was invented from a name and sent operators to the
  // wrong control for a year.
  EngineBusy: {
    // 503. The contract is explicit: "retry, and do NOT present it as a failed
    // plan. It is the same class as a queue being full." Nothing is wrong with
    // the layout — it was never run. Copy that said "failed" would train
    // operators to redraw a carton that is already correct.
    title: 'The layout server is busy',
    hint: 'Every nesting slot is in use right now. Nothing is wrong with this layout — '
      + 'wait a few seconds and run it again.',
  },
  EngineUnreachable: {
    // 502, FATAL. The contract says surface it and report it; "a retry loop
    // here would hide an outage". So the hint deliberately does NOT say "try
    // again" — a room full of operators retrying is how an outage stays
    // invisible.
    title: 'The layout server is not responding',
    hint: 'This is an outage on our side, not a problem with your carton. Please report it.',
  },
  EngineEndpointInvalid: {
    // 500. A deployment misconfiguration: "not the caller's fault and not
    // retryable by them". Same destination as PrepressResidue — report it.
    title: 'Something is misconfigured on our side',
    hint: 'The server is pointed at a layout engine it cannot use. Nothing is wrong '
      + 'with your carton — please report it.',
  },
  LayoutTimeout: {
    // 504. NOT `time_limit` expiring, which is not an error at all and returns
    // the best layout found so far. This is the hard wall clock, and the
    // worker was killed — so there is no partial result to show.
    title: 'This layout took too long and was stopped',
    hint: 'Try again with fewer pieces or a simpler sheet. If it keeps happening, '
      + 'report it — the time budget may need raising for work this size.',
  },
  UnflappedCartonNotTagged: {
    // 400, and the only one of these the operator can actually fix. The
    // contract also asks for a client-side rail guard off
    // `unflapped_vertex_counts`; that is NOT implemented here yet, so this copy
    // is currently the whole of our handling. It names the fix rather than the
    // rule, because "6/8/10/12 corners" is our vocabulary, not theirs.
    title: 'This carton still needs its flaps',
    hint: "Turn on prepress for this piece so the flaps are added. As drawn it would "
      + 'plate a die that cannot be folded.',
  },
  // ── diagnostic surfaces, like TraceNotFound ────────────────────────────
  // The contract publishes these and says in the same breath that they are not
  // part of the client contract (/debug/trace). An operator seeing one means
  // the app called something it should not have — our bug, not their mistake.
  PageMissing: {
    title: 'Something went wrong on our side',
    hint: 'A diagnostic page could not be loaded. Nothing is wrong with your layout — '
      + 'please report it.',
  },
  UnknownLayer: {
    title: 'Something went wrong on our side',
    hint: 'A diagnostic view was asked for something that does not exist. Nothing is '
      + 'wrong with your layout — please report it.',
  },
  TraceNotFound: {
    // ⑭j. The contract publishes this code and then says, in the same breath,
    // that it does NOT belong to the client contract — it is the /debug/trace
    // troubleshooting surface. So an operator seeing it means something called
    // an endpoint it should not have, which is our bug, not their mistake.
    //
    // Copy exists because the contract diff requires every published code to
    // have some, and because a bare "404" would send an operator hunting for a
    // missing carton. It routes to "report this", like PrepressResidue.
    title: 'Something went wrong on our side',
    hint: 'This is a diagnostic surface the app should not be calling. Nothing is '
      + 'wrong with your layout — please report it.',
  },
  // ── the admin flap-slab table (contract 1.13, V2 #54) ──
  // A superuser sets the flap sizes in the Control Center; these are the
  // refusals an operator can meet because of it. The server's message names
  // the carton, the flap and the row, which no copy written here could.
  // (SlabDomainError, which a table can also raise, already has copy above.)
  CartonOutsideSlabTable: {
    title: 'No flap sizes are set for a carton this size',
    preferServerMessage: true,
    hint: "This carton's side panel is narrower or wider than every row of the flap-size table. An admin can add a row for it in the Control Center.",
  },
  FlapRangeOutsideSlab: {
    title: 'A flap setting is outside the allowed sizes',
    preferServerMessage: true,
    hint: "Keep the slider inside the range shown for this flap. A saved product's flap range may also need re-saving inside it.",
  },
  FlapSlabsNotClientSettable: {
    // The app never sends `flap_slabs`; only a stale tab could. Like the
    // passthrough refusal, the action is a reload.
    title: 'This page is out of date',
    hint: 'Flap sizes are set by an admin in the Control Center. Reload the page and try again.',
  },
  SlabTableError: {
    title: 'Something went wrong on our side',
    hint: 'The flap-size table could not be read by the layout engine. Nothing is wrong with your layout — please report it.',
  },
  ConfigError: {
    title: 'Prepress could not run with these settings',
    // ⑭f / rev 7 §4: this code has TWO origins and `code` alone cannot tell
    // them apart. The package raises it for an unrecognised key or an
    // out-of-range value; the backend raises it when a field the RUN needs is
    // missing — `auto` without a sheet is the published example — and there
    // `message` names the key.
    //
    // The old hint ("reset the prepress config") was right for the first
    // origin and sent an operator to the wrong control for the second. So we
    // stop guessing and let the server's own message speak. The hint below is
    // only for a body that carries no message at all.
    preferServerMessage: true,
    hint: 'A prepress setting was rejected, or a field this run needs is missing.',
  },
  PrepressPieceError: {
    title: 'Prepress failed for this carton',
    hint: null,       // wrapper — surface the server message as-is
  },

  // ── backend guards (reply §2.1) ──
  AutoRequiresAsync: {
    title: 'Automatic mode needs a background job',
    hint: 'Automatic nests the sheet once per candidate and takes 20–30 seconds, so it cannot run on an interactive request.',
  },
  TilingDisabled: {
    title: 'Paired layouts are not enabled',
    hint: 'Interlocked tiles enclose a scrap pocket that a die-maker must confirm is strippable. Use plain mode until that sign-off is in.',
  },
  ObjectiveIncompatibleWithTiling: {
    title: 'That objective cannot be used with paired layouts',
    hint: 'Tiles inflate gross utilization. Choose "fewest sheets" or "least overrun" instead.',
  },
  UnsupportedCartonType: {
    title: 'This carton type is not supported',
    hint: 'Prepress currently handles BL cartons only — no inner-lock or tuck-in styles.',
  },
  UnsupportedMode: {
    title: 'Unknown prepress mode',
    hint: 'Choose plain, paired, or automatic.',
  },
  PrepressUnavailable: {
    title: 'Prepress is not available on this server',
    hint: 'The prepress package is not installed. Remove the prepress tags or contact your administrator.',
  },

  DuplicateCartonName: {
    title: 'Two tagged cartons share a name',
    // Report entries correlate by name, so a collision would attach one
    // carton's dieline to another. We guard client-side, but names can still
    // collide only AFTER the extension is stripped — so the copy has to exist.
    hint: 'Prepress matches its report to cartons by name. Rename one so each tagged carton is unique.',
  },

  // ── rev 4 ──
  PrepressSpecError: {
    title: 'The prepress request was malformed',
    // One code for every malformed spec, with a precise message written for an
    // operator — so surface the server's text rather than canned copy.
    hint: null,
  },
  // ⑭l / rev 12 §0.1 — the refusal that replaced the package's own
  // "BL base must be exactly 6 (x, y) vertices", which was accurate about the
  // consequence and named the wrong family. The message is written for an
  // operator: it names the family whose topology matches and says whether the
  // type was supplied or defaulted. So `hint` is null and the server's text is
  // surfaced verbatim — ⑭f's lesson, applied on the way in this time.
  CartonTypeTopologyMismatch: {
    title: 'This outline does not match its carton type',
    hint: null,
  },
  PrepressUnsupportedOnRoute: {
    title: 'This route does not support prepress',
    hint: 'The request reached an engine route that cannot generate dielines. Use the standard layout path.',
  },
  RequiredFlagMisplaced: {
    title: 'The firm-line mark is in the wrong place',
    hint: "`required` belongs on the piece itself, not inside its prepress block — where it would be silently ignored.",
  },
  RequiredRequiresAuto: {
    title: 'A firm order line needs automatic mode',
    hint: 'Protecting a firm line means measuring the alternatives. Switch that carton to automatic, or clear the firm mark.',
  },
  NotAwaitingConfirmation: {
    title: 'This job has nothing to confirm',
    hint: 'It already finished, or it never halted. Run the layout again rather than confirming this one.',
  },
  ConfirmRequiresResubmit: {
    title: 'The uploads were not kept — send them again',
    hint: 'This server does not store uploads between attempts. Run the layout again; your confirmation is remembered.',
  },
  PrepressResidue: {
    // A 500, and explicitly the backend's defect. Deliberately worded so nobody
    // goes hunting through artwork for a bug in someone else's code.
    title: 'Prepress did not run — this is a server-side defect',
    hint: 'The server caught itself about to nest an unflapped outline and refused. Report this; there is nothing wrong with your artwork.',
  },
};

/** Codes that are the backend's defect, not the operator's input. */
export const BACKEND_DEFECT_CODES = ['PrepressResidue'];

/** True when this failure is ours to report, not the operator's to fix. */
export function isBackendDefect(body) {
  return BACKEND_DEFECT_CODES.includes(parsePrepressError(body)?.code);
}

/**
 * Pull the structured prepress error out of a parsed response body.
 *
 * `detail` now has three possible shapes:
 *   - object  → prepress error   { code, message, piece }
 *   - string  → legacy 400
 *   - array   → Pydantic 422
 * Only the first is ours.
 *
 * @returns {{ code: string, message: string, piece: string|null }|null}
 */
export function parsePrepressError(body) {
  const d = body?.detail;
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
  if (typeof d.code !== 'string' || d.code === '') return null;
  return {
    code: d.code,
    // Rev 5 §9.2: `PrepressPieceError` is a SIBLING of the specific classes,
    // not a parent — the real reason lives on the wrapped cause. Absent, not
    // null, when there is no cause.
    causeCode: typeof d.cause_code === 'string' && d.cause_code !== '' ? d.cause_code : null,
    message: typeof d.message === 'string' ? d.message : '',
    piece: typeof d.piece === 'string' && d.piece !== '' ? d.piece : null,
  };
}

/**
 * Full operator-facing description of a prepress error, or null if the body
 * isn't one. `piece` names the carton row to highlight.
 *
 * @returns {{ code, piece, title, hint, technical }|null}
 */
export function describePrepressError(body) {
  const parsed = parsePrepressError(body);
  if (!parsed) return null;
  // ⑭c: a halt is NOT an error and must never borrow error copy. If one ever
  // reaches an error path, the caller falls through to the server's own
  // message — which is truthful — rather than rendering "Prepress rejected
  // this carton" over a job the operator can still choose to run.
  if (parsed.code === CONFIRMATION_CODE) return null;

  // ⑭a / rev 5 §9.2: prefer the WRAPPED cause for operator copy. A
  // `PrepressPieceError` wrapping a `PoseError` used to render the generic
  // wrapper title — "Prepress failed for this carton" — instead of the copy
  // that tells the operator their drawing is mirrored and what to do about it.
  // Fall back to `code` when there is no cause, so existing bodies are
  // untouched.
  const copy = (parsed.causeCode && PREPRESS_ERROR_COPY[parsed.causeCode])
    || PREPRESS_ERROR_COPY[parsed.code];
  return {
    code: parsed.code,
    causeCode: parsed.causeCode,
    piece: parsed.piece,
    title: copy?.title || 'Prepress rejected this carton',
    // A mapped hint normally beats the server's message, because ours says
    // what to DO about it. ⑭f: for a code whose message is more specific than
    // any copy we could write, that precedence is backwards — and a hint that
    // shadows the more specific sentence is worse than no hint at all.
    // `preferServerMessage` inverts it for that ONE entry rather than
    // globally, which would discard every piece of operator copy we have.
    //
    // No mapped hint (unknown or wrapper code) → fall back to the server's own
    // message, which always names the piece.
    hint: (copy?.preferServerMessage && parsed.message)
      ? parsed.message
      : (copy?.hint || parsed.message || null),
    technical: parsed.message || parsed.causeCode || parsed.code,
  };
}

/** One-line message for toasts/tray rows. Prefixed with the carton when known. */
export function prepressErrorLine(body) {
  const d = describePrepressError(body);
  if (!d) return null;
  const head = d.piece ? `${d.piece}: ${d.title}` : d.title;
  return d.hint ? `${head} — ${d.hint}` : head;
}

/* ------------------------------------------------------------------ */
/* Firm order lines — `required` (rev 5 §6) — PR ⑭d                    */
/* ------------------------------------------------------------------ */

/**
 * Serialize the per-carton `required` flags for the export path.
 *
 * THE THIRD MEMBER OF THE ENCODING FAMILY, and it belongs with `prepress`:
 *
 *   prepress            scalar is a STRING   → a flat list is POSITIONAL
 *   required            scalar is a BOOLEAN  → a flat list is POSITIONAL
 *   allowed_rotations   scalar is a LIST     → a flat list BROADCASTS
 *
 * `[true,false]` and `[0,180]` are both flat JSON lists and mean opposite
 * things. All three are pinned side by side in the tests for that reason.
 *
 * Omits entirely when nothing is required — `required=false` on every job would
 * change the content hash of every untagged run for no gain.
 *
 * @param {Array<boolean>} flags  aligned to file order
 * @returns {string|null}
 */
/**
 * `CartonTypeTopologyMismatch`'s detail fields (rev 12 §0.1).
 *
 * `suggested_carton_types` is what the operator should be OFFERED — never what
 * we apply. The contract says so in as many words, and gives the reason: the
 * topologies happen to be disjoint among the types supported today, but IL
 * shares BL's 6 vertices and its reinstatement trigger is still live, so any
 * automatic mapping acquires a silently wrong branch the day IL returns.
 *
 * @returns {{ suggested: string[], detectedVertexCount: number|null,
 *             wasDefaulted: boolean, typeUsed: string|null }|null}
 */
export function parseTopologyMismatch(body) {
  const d = body?.detail && typeof body.detail === 'object' ? body.detail
    : (body?.error_detail && typeof body.error_detail === 'object' ? body.error_detail : null);
  if (!d || d.code !== 'CartonTypeTopologyMismatch') return null;
  return {
    suggested: Array.isArray(d.suggested_carton_types)
      ? d.suggested_carton_types.filter((t) => typeof t === 'string' && t) : [],
    detectedVertexCount: Number.isFinite(d.detected_vertex_count) ? d.detected_vertex_count : null,
    wasDefaulted: d.carton_type_was_defaulted === true,
    typeUsed: typeof d.carton_type_used === 'string' ? d.carton_type_used : null,
  };
}

export function serializeRequired(flags) {
  const list = (Array.isArray(flags) ? flags : []).map((f) => f === true);
  if (list.length === 0 || !list.some(Boolean)) return null;
  if (list.every(Boolean)) return 'true';        // broadcast
  return JSON.stringify(list);                   // positional
}

/**
 * `required` is only evaluated in `auto`. The export path REJECTS it with
 * `400 RequiredRequiresAuto` — you chose the mode in the same request, so an
 * inert flag there is a mistake rather than a leftover.
 *
 * Caught client-side because the alternative is uploading every carton PDF to
 * be told so. Same reasoning as the duplicate-name guard.
 *
 * ONLY blocks a carton that is TAGGED with a non-auto mode. A firm line with no
 * prepress tag at all is not an error here — it is precisely the case the
 * confirmation handshake exists for (rev 5 §7.1: required, no prepress block,
 * still a bare 6-corner L). Blocking it client-side would make the halt
 * unreachable and quietly delete the feature.
 *
 * @returns {string|null} operator copy, or null when there is nothing to block
 */
/**
 * An UNTAGGED carton whose outline is still an unflapped input shape.
 *
 * PRODUCT RULE, and it deliberately does not read `required`. Nesting a bare
 * keyline plates a die with no flaps: the sheet is fine, the carton cannot be
 * folded, the run is scrap. Whether the line is firm decides what a shortfall
 * COSTS; it has nothing to do with whether the geometry is manufacturable, and
 * coupling the two meant an identical carton nested silently whenever nobody
 * happened to mark it.
 *
 * Mirrors `assert_flapped_or_tagged`, which refuses this on the export path —
 * so this is a courtesy over a real rejection, not a veto of our own (⑭d §2).
 * The counts come from published `unflapped_vertex_counts`, never a literal:
 * the accepted set has both widened and narrowed in this project's life.
 *
 * Returns null when there is nothing to report, including when capabilities
 * have not loaded — we publish no opinion rather than blocking the bench on a
 * rule we cannot yet read.
 */
export function unflappedUntaggedReason(carton, caps) {
  if (carton?.prepress) return null;            // tagged — prepress will flap it
  const counts = acceptedUnflappedCounts(caps);
  if (!counts || counts.length === 0) return null;
  const n = outlineCornerCount(carton?.detection?.vertices);
  if (n === null || !counts.includes(n)) return null;   // flaps drawn by hand
  const families = offerableCartonTypes(caps)
    .filter((t) => (unflappedCountsFor(caps, t) || []).includes(n));
  const who = families.length ? `${families.join(' or ')}` : 'a carton';
  return `This outline is unflapped (${n} corners) — the input shape for `
    + `${who} — and "Generate dieline" is off. Nesting it would plate a die `
    + `with no flaps, which cannot be folded. Tick "Generate dieline" on this row.`;
}

/** Cartons that would plate an unfoldable die. Blocks Run layout outright. */
export function unflappedUntaggedBlockReason(cartons, caps) {
  const bad = (Array.isArray(cartons) ? cartons : [])
    .filter((c) => unflappedUntaggedReason(c, caps) !== null);
  if (bad.length === 0) return null;
  const names = bad.map((c) => sourceIdOf(c) || 'a carton');
  return names.length === 1
    ? `${names[0]}: unflapped outline with no dieline. Tick "Generate dieline" on that row.`
    : `Unflapped outlines with no dieline on: ${names.join(', ')}. `
      + 'Tick "Generate dieline" on those rows.';
}

export function firmLineHonoured(carton, cartons, caps) {
  const own = requiredOwnershipOf(caps);
  const mode = normalizeMode(carton?.prepress);
  if (mode === null) {
    // UNTAGGED. Not derivable from `modesEvaluated` — there is no mode to look
    // up — which is why the backend publishes `untagged_evaluated` separately.
    if (own.untaggedEvaluated === 'always') return { honoured: true, reason: null };
    const jobHasAuto = (Array.isArray(cartons) ? cartons : [])
      .some((c) => normalizeMode(c?.prepress) === 'auto');
    if (jobHasAuto) return { honoured: true, reason: null };
    return {
      honoured: false,
      reason: 'nothing in this job is set to automatic, so no candidate '
        + 'selection runs and the firm mark is never read. Set one carton to '
        + 'automatic, or clear the firm mark.',
    };
  }
  if (own.modesEvaluated.includes(mode)) return { honoured: true, reason: null };
  return {
    honoured: false,
    reason: `its prepress mode is '${mode}', and on this deployment only `
      + `${own.modesEvaluated.join('/')} evaluates a firm order line. `
      + 'Switch it, or clear the firm mark.',
  };
}

/**
 * Cartons marked firm whose mark this deployment will NOT honour.
 *
 * REPLACES `requiredWithoutAutoBlockReason`, which hard-coded `auto`. That
 * guard was CORRECT when written — it mirrored a real server rejection — and
 * ⑭d §2 records the lesson it was already trimmed by once: *"a client-side
 * guard mirroring a server rejection is safe only where the server actually
 * rejects. One case too wide and it stops being a courtesy and silently
 * becomes a veto — and it fails closed, invisibly."*
 *
 * `required_passthrough` retired that rejection for every mode, and the mirror
 * was never updated, so on a passthrough deployment it vetoed `plain`+firm and
 * `pair`+firm — both of which the backend accepts and MNE honours (measured:
 * the flag survives `apply_prepress` intact, and on `pair` it rides onto the
 * fused `__pair` piece). Reading the rule off capabilities is the only shape
 * that cannot drift again, and it is the same rule the carton-type picker
 * already follows.
 *
 * Job-scoped on purpose: under legacy an untagged firm line is honoured only
 * when SOMETHING in the job is `auto`, which no per-carton predicate can see.
 */
export function firmLineBlockReason(cartons, caps) {
  const list = Array.isArray(cartons) ? cartons : [];
  const bad = list
    .filter((c) => c?.required === true)
    .map((c) => ({ c, v: firmLineHonoured(c, list, caps) }))
    .filter((x) => !x.v.honoured);
  if (bad.length === 0) return null;
  const name = (x) => sourceIdOf(x.c) || 'a carton';
  if (bad.length === 1) {
    return `${name(bad[0])} is marked as a firm order line, but ${bad[0].v.reason}`;
  }
  return `${bad.length} cartons are marked as firm order lines but will not be `
    + `honoured: ${bad.map(name).join(', ')}. ${bad[0].v.reason}`;
}

/**
 * The one prepress outcome that deliberately produces LESS than was ordered.
 *
 * When protecting a firm line costs cartons, prepress retries at reduced
 * counts. Rev 5 §6.2: showing this is not optional — the operator ordered N and
 * is getting capacity for fewer, and they must see both numbers before they
 * quote or release.
 *
 * @returns {{ requested: number|null, capacity: number|null, shortfall: number|null }|null}
 */
export function reducedCapacity(result) {
  if (result?.prepress_reduced_to_meet_requirements !== true) return null;
  const requested = Number.isFinite(result?.prepress_requested_cartons)
    ? result.prepress_requested_cartons : null;
  const capacity = Number.isFinite(result?.prepress_produced_cartons_capacity)
    ? result.prepress_produced_cartons_capacity : null;
  return {
    requested,
    capacity,
    shortfall: (requested !== null && capacity !== null) ? requested - capacity : null,
  };
}

/**
 * Operator copy for a reduction. Deliberately states BOTH numbers rather than
 * only the shortfall — "3 fewer" is a different sentence from "you ordered 5
 * and are getting 2", and only the second one stops a quote.
 */
export function reducedCapacityNotice(result) {
  const r = reducedCapacity(result);
  if (!r) return null;
  if (r.requested === null || r.capacity === null) {
    return 'This layout was reduced to protect a firm order line. Do not quote from it '
      + 'until you have confirmed the counts.';
  }
  return `Ordered ${r.requested} — this sheet gives capacity for ${r.capacity}. `
    + 'Prepress reduced the run to protect a firm order line. Check this before quoting or releasing.';
}

/**
 * WHICH order line was cut short (rev 7 §8) — ⑭f.
 *
 * The three job-wide fields say a shortfall happened. They do not say whose,
 * and with two firm lines the operator's next action is a phone call to one
 * specific customer. This names them.
 *
 * Still gated on the FLAG, exactly as ⑭d gated the numbers: a map on its own
 * never implies a reduction, because `reduced: false` entries ride along in
 * the ordinary case and reading the map as the trigger would raise the banner
 * on every firm job.
 *
 * @returns {Array<{ name, requested, capacity, shortfall }>}  reduced entries only
 */
export function reducedByCarton(result) {
  if (result?.prepress_reduced_to_meet_requirements !== true) return [];
  const map = result?.prepress_reduced_by_carton;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
  return Object.entries(map)
    .filter(([name, v]) => name && v && typeof v === 'object' && !Array.isArray(v) && v.reduced === true)
    .map(([name, v]) => {
      const requested = Number.isFinite(v.requested) ? v.requested : null;
      const capacity = Number.isFinite(v.capacity) ? v.capacity : null;
      return {
        name,
        requested,
        capacity,
        shortfall: (requested !== null && capacity !== null) ? requested - capacity : null,
      };
    });
}

/**
 * Copy for the case where the server sent an attribution map that names nobody.
 *
 * Two absences that look identical on screen must not read identically:
 *
 *   map ABSENT   → an older deployment. ⑭d's banner is complete as it stands,
 *                  and adding a line about it would invent a problem.
 *   map PRESENT, nothing marked reduced → the flag and the map disagree. Saying
 *                  nothing here reads as "no line was affected", which is the
 *                  one reading that lets a reduced run be quoted normally.
 */
export function reducedAttributionNotice(result) {
  if (result?.prepress_reduced_to_meet_requirements !== true) return null;
  const map = result?.prepress_reduced_by_carton;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  if (reducedByCarton(result).length) return null;
  return 'The server did not say which order line was reduced. Confirm the per-carton '
    + 'counts before quoting.';
}

/* ------------------------------------------------------------------ */
/* The confirmation handshake (rev 5 §7, rev 6 §1–§2) — PR ⑭c          */
/* ------------------------------------------------------------------ */

/**
 * A halt is not a failure. It is the server saying a firm order line would be
 * nested without flaps, and asking whether that is really intended.
 */
export const CONFIRMATION_CODE = 'PrepressConfirmationRequired';

/**
 * ONE reader for both carriers.
 *
 * The sync route puts the halt on `detail` of a 409. The async route puts the
 * SAME object on `error_detail` of a job whose `status` is `failed`. Backend
 * made the shapes identical deliberately, and rev 6 §1 asks for a single
 * reader — so this takes either an error body or a job record.
 *
 * `status: "failed"` on a halted job is not a mistake to work around: a new
 * status would have been treated as non-terminal by every existing poll loop,
 * including ours, which inverts the test and would have kept polling forever.
 *
 * @param {object} source  an error body ({detail}) or a job record ({error_detail})
 * @returns {{ ackToken: string|null, warnings: Array, message: string,
 *             confirm: string|null }|null}
 */
export function parseConfirmationRequired(source) {
  const d = source?.detail ?? source?.error_detail ?? null;
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
  if (d.code !== CONFIRMATION_CODE) return null;
  return {
    ackToken: typeof d.ack_token === 'string' && d.ack_token ? d.ack_token : null,
    warnings: normalizeWarnings(d.warnings),
    message: typeof d.message === 'string' ? d.message : '',
    // ⑭f / rev 7 §6: the halt carries its own resume URL, so the client stops
    // building one. Null when the server did not send it — the caller then
    // resubmits with the ack (rev 6 §2.2), which costs an upload but is a
    // documented route rather than a hand-assembled path.
    confirm: jobPathFromResponse(d.confirm),
  };
}

/**
 * A server-supplied job path, or null.
 *
 * ⑭f: following `error_detail.confirm` makes this the first server-supplied
 * string we POST to, and that request carries the bearer token AND the ack.
 * So it is checked before it is used: rooted at `/`, same origin, no traversal.
 *
 * An absolute or protocol-relative URL would send both credentials to another
 * host; a `..` segment would let a response steer the request off the job
 * routes. Neither is a shape the backend emits — which is exactly why the
 * check costs nothing and why a string failing it is treated as no URL at all
 * rather than as an error. The caller falls back to the documented resubmit.
 */
export function jobPathFromResponse(value) {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || raw[0] !== '/') return null;        // relative, or an absolute URL
  if (raw[1] === '/') return null;                // protocol-relative → another host
  if (/[\s\\]/.test(raw)) return null;            // whitespace or a backslash escape
  const [path] = raw.split('?');
  if (path.split('/').includes('..')) return null;
  return raw;
}

/** True when this body or job is a halt awaiting confirmation. */
export function isConfirmationRequired(source) {
  return parseConfirmationRequired(source) !== null;
}

/**
 * Warnings the PLANNER run produced (rev 7 §7) — ⑭f.
 *
 * An inert `required` — a firm line whose SKU is not on `auto`, which the
 * planner warns about rather than rejecting — reached `logger.info` and
 * nothing else. An operator was never told the flag did nothing, which defeats
 * the point of warning instead of rejecting.
 *
 * Deliberately the SAME per-entry shape as the confirmation warnings, so it
 * reuses the same normaliser and inherits the verbatim-message rule with it.
 *
 * Planner path only. Rev 7 §7 scopes it there, and the export path REJECTS
 * this condition (`RequiredRequiresAuto`) rather than warning about it — so
 * reading the key on an export response would be reading for something that
 * cannot arrive.
 */
export function prepressWarnings(result) {
  return normalizeWarnings(result?.prepress_warnings);
}

function normalizeWarnings(list) {
  return (Array.isArray(list) ? list : [])
    .filter((w) => w && typeof w === 'object')
    .map((w) => ({
      code: typeof w.code === 'string' ? w.code : null,
      piece: typeof w.piece === 'string' && w.piece ? w.piece : null,
      // Surfaced VERBATIM. This is the sentence that tells an operator which
      // carton would be cut without flaps; paraphrasing it loses the piece
      // name and the reason at the same time.
      message: typeof w.message === 'string' ? w.message : '',
    }));
}

/**
 * The pieces a preflight check needs. Bodiless by design (rev 6 §2.1) — names,
 * corner counts and flags only, no geometry and no file bytes, so stage 1 can
 * run BEFORE a multi-megabyte upload rather than after it.
 */
export function preflightPieces(cartons) {
  return (Array.isArray(cartons) ? cartons : [])
    .map((c) => ({
      name: sourceIdOf(c),
      vertex_count: Array.isArray(c?.detection?.vertices) ? c.detection.vertices.length : null,
      required: c?.required === true,
      tagged: Boolean(normalizeMode(c?.prepress)),
    }))
    .filter((p) => p.name && Number.isFinite(p.vertex_count));
}

/**
 * Whether a preflight is worth the round trip.
 *
 * Stage 1 only ever warns about a piece that is `required`. With none, the
 * answer is guaranteed to be an empty warning list, so asking costs a request
 * and tells us nothing. The real submit still runs stage 1 either way —
 * preflight saves an upload, it never skips the check.
 *
 * Nothing sets `required` until ⑭d, so this returns false today. That is the
 * correct behaviour now AND after ⑭d, with no further change.
 */
export function needsPreflight(pieces) {
  return (Array.isArray(pieces) ? pieces : []).some((p) => p?.required === true);
}

/** Normalize `POST /planner/prepress/preflight`. Same shape as a halt. */
export function parsePreflight(body) {
  if (!body || typeof body !== 'object') return null;
  return {
    ackToken: typeof body.ack_token === 'string' && body.ack_token ? body.ack_token : null,
    warnings: normalizeWarnings(body.warnings),
    message: '',
  };
}

/** True when a preflight came back with something the operator must see. */
export function preflightHalts(pre) {
  return Boolean(pre && pre.warnings.length > 0 && pre.ackToken);
}

/* ------------------------------------------------------------------ */
/* UX affordance — bare-L detection (reply §3)                         */
/* ------------------------------------------------------------------ */

/**
 * Whether a detected outline LOOKS like a bare structural L, so the UI can
 * decide whether to offer the prepress tag at all.
 *
 * This is an affordance, NOT validation. A 6-vertex shape is not necessarily a
 * valid base L — pose, degeneracy and tolerance are all still checked
 * server-side, and the 400 is authoritative. It exists to catch the common
 * mistake (tagging a finished CorelDRAW dieline) without a round trip.
 */
export function outlineCornerCount(polygon) {
  if (!Array.isArray(polygon)) return null;
  const pts = polygon.filter(
    (p) => Array.isArray(p) && p.length >= 2
      && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  );
  if (pts.length !== polygon.length) return null;

  // Tolerate an explicit closing vertex that repeats the first point.
  let n = pts.length;
  if (n > 1) {
    const [fx, fy] = pts[0];
    const [lx, ly] = pts[n - 1];
    if (fx === lx && fy === ly) n -= 1;
  }
  return n;
}

/**
 * Whether this outline is one of the UNFLAPPED topologies the deployment
 * accepts, so the UI can decide whether to offer the prepress tag at all.
 *
 * ⑭k — THIS TESTED FOR 6 AND ONLY 6, AND THAT MADE TWO FAMILIES UNREACHABLE.
 *
 * rev 12 §1: RTE takes 10 corners, STE takes 8 or 12. Both were read here as
 * "a finished dieline", the tag was never offered, and there was no way to
 * tag a tuck carton from this client at all — the feature backend shipped was
 * unreachable through the UI, and the message told the operator their perfectly
 * good outline had the wrong number of corners.
 *
 * The counts come from `capabilities.unflapped_vertex_counts`. A deployment
 * that publishes none falls back to `{BL:[6]}`, which is exactly today's
 * behaviour on a deployment that has not shipped rev 12.
 *
 * Still an AFFORDANCE, not validation. Pose, degeneracy and tolerance are all
 * checked server-side and the 400 is authoritative. Note what rev 12 §2 costs
 * it: "more than 6 corners" is no longer evidence of a hand-drawn dieline, so
 * this test discriminates less than it used to and is worth less than it was.
 */
export function looksUnflapped(polygon, caps) {
  const n = outlineCornerCount(polygon);
  if (n === null) return false;
  return acceptedUnflappedCounts(caps).includes(n);
}

/**
 * Does this carton still need its flaps put on?
 *
 * The Layout Editor's desk takes cartons straight from an upload or the
 * library, and nothing has run prepress on those. An unflapped outline placed
 * as drawn is a die with no glue flap, no tuck and no dust flap — it nests
 * against its neighbours by the wrong shape and, if it reaches a plate, is cut
 * as a carton that cannot be made up. That is a silent wrong answer, so the
 * editor asks before placing rather than after.
 *
 * The test is `looksUnflapped`, which is the same AFFORDANCE the Layout rail
 * uses to decide whether to offer the prepress tag — a corner count against the
 * deployment's published unflapped topologies, not a verdict. Two honest
 * limits come with it, both from rev 12 §2: a hand-drawn dieline that happens
 * to land on an accepted count reads as unflapped, and a family this server
 * publishes no topology for cannot be recognised at all. Which is why the
 * answer is a QUESTION put to the operator and never an automatic transform:
 * "place as drawn" has to stay one click away, because sometimes it is right.
 *
 * `cartonType` is pre-chosen ONLY when exactly one offered family takes this
 * count. Two candidates is a real ambiguity — rev 12 §1.3 is explicit that a
 * family cannot be read off a vertex count — and guessing one would send
 * `carton_type` the operator never picked, which is how an 8-corner STE got
 * cut as a BL.
 *
 * @returns {{ corners, suggested, cartonType }|null}  null = nothing to ask.
 */
export function flapsMissing(polygon, caps) {
  if (!looksUnflapped(polygon, caps)) return null;
  const suggested = suggestedCartonTypes(caps, polygon);
  return {
    corners: outlineCornerCount(polygon),
    suggested,
    cartonType: suggested.length === 1 ? suggested[0] : null,
  };
}

/** Why the prepress tag is unavailable for this outline, or null if it is. */
export function unflappedBlockReason(polygon, caps) {
  if (looksUnflapped(polygon, caps)) return null;
  const n = outlineCornerCount(polygon);
  if (n === null || n === 0) return 'No outline detected yet.';
  const accepted = acceptedUnflappedCounts(caps);
  const list = accepted.length > 1
    ? `${accepted.slice(0, -1).join(', ')} or ${accepted[accepted.length - 1]}`
    : String(accepted[0]);
  if (n > Math.max(...accepted)) {
    return `This looks like a finished dieline (${n} corners). Prepress needs an unflapped outline — ${list} corners on this server — and adds the flaps itself.`;
  }
  return `This outline has ${n} corners. Prepress takes ${list} corners on this server.`;
}

/* ------------------------------------------------------------------ */
/* Placement geometry (W5) — what a placed piece is really made of     */
/* ------------------------------------------------------------------ */

/**
 * A tile is ONE placed piece and TWO interlocked cartons. `placed_vertices`
 * is the FUSED outline — the shape of the die, not of what comes off it — so
 * a renderer that draws only that shows one solid block where the press cuts
 * two dielines. That is what our sheet view did: a paired carton appeared as
 * a single rectangle twice the size of the carton it represents, with one
 * label, and nothing on screen said otherwise.
 *
 * `placements[].components[]` is the backend's answer: each real carton's own
 * outline, ALREADY in sheet coordinates (contract §3.5 — same frame as
 * `placed_vertices`, transform already applied). Never re-apply
 * `rotation_deg` or `translation` to these.
 *
 * An EMPTY components array means "not established" — an older backend, or
 * one whose round-trip check refused. It never means the piece is empty, so
 * the fallback is the fused outline drawn as one carton, which is what this
 * client did before and is at least not wrong about the die.
 */
export function placementCartons(placement) {
  if (!placement || typeof placement !== 'object') return [];
  const comps = Array.isArray(placement.components) ? placement.components : [];
  const usable = comps.filter((c) => Array.isArray(c?.placed_vertices)
                                  && c.placed_vertices.length >= 3);
  if (usable.length) {
    return usable.map((c, i) => ({
      vertices: c.placed_vertices,
      role: typeof c.role === 'string' ? c.role : null,
      member: Number.isInteger(c.member) ? c.member : i,
      fused: false,
    }));
  }
  const outline = placement.placed_vertices;
  if (!Array.isArray(outline) || outline.length < 3) return [];
  return [{ vertices: outline, role: null, member: 0, fused: true }];
}

/**
 * Is this placement drawn as the fused die rather than as its cartons?
 *
 * True only when the piece IS worth more than one carton and the split is
 * missing — i.e. the one case where the picture under-describes the sheet.
 * A plain single carton is not "fused"; its outline is the whole truth.
 */
export function placementIsUnsplit(placement) {
  const cartons = Number(placement?.cartons);
  if (!Number.isFinite(cartons) || cartons < 2) return false;
  return placementCartons(placement).every((c) => c.fused);
}

/**
 * The carton name to print on a placement.
 *
 * `source_id` on the placement is AUTHORITATIVE. Indexing `sources[]` by
 * `type_index` is the mapping a tile breaks — one source emits `<n>__pair`
 * AND `<n>`, so every index above it shifts by one and one carton renders
 * under another's name (the same defect that removed `emittedGeometryFor`).
 * The index lookup survives only as a fallback for backends that predate
 * `source_id`, where nothing has tiled and the indices therefore still line up.
 */
export function placementSourceLabel(placement, sources) {
  if (typeof placement?.source_id === 'string' && placement.source_id) {
    return placement.source_id;
  }
  const ti = placement?.type_index;
  const src = Array.isArray(sources) && Number.isInteger(ti) ? sources[ti] : null;
  if (src && typeof src.source_id === 'string' && src.source_id) return src.source_id;
  return Number.isInteger(ti) ? `type ${ti}` : 'unknown';
}

/**
 * How many real cartons a placement is worth (contract §6 at placement
 * granularity). Defaults to 1, which is what every non-tiled piece is and
 * what every backend that predates the field means.
 */
export function placementCartonCount(placement) {
  const n = Number(placement?.cartons);
  return Number.isInteger(n) && n > 0 ? n : 1;
}
